﻿import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import { AuditPortalLoginPage } from './LoginPage.jsx';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import { 
  User, Lock, Mail, LogIn, LogOut, UserPlus, Settings,
  Eye, EyeOff, Shield, Check, X, Edit2, Save, Camera, Sparkles,
  Users, Trash2, Search, UserCog, RefreshCw, Key,   ChevronDown, ChevronUp, ChevronLeft, FilePlus, Plus, Copy, Info
} from 'lucide-react';
import { auditEventBus, AUDIT_EVENTS } from '../../services/auditModuleService';
import { addLog } from '../../services/auditLogService';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  MODULE_ID_TO_SHORTNAME,
  ALL_PERMISSION_MODULES,
  MODULE_OPERATION_TYPES,
  AUDIT_ROLES,
  AUDIT_GROUPS,
  getAuditRolePermissionSummary,
  getAuditRoleOperationSummary,
  getModuleOperationsForUser,
  canModuleOperation,
  getModelLibraryAllowedModelIds
} from '../../constants/auditRolesAndGroups';
import {
  MODULE_PERMISSIONS,
  GROUP_ROLES,
  validatePasswordPolicy,
  getUserAuditGroup,
  isGroupLeader,
  getGroupMembers,
  getUsers,
  RISK_TAB_OPTIONS,
  INTERNAL_CONTROL_MODULE_OPTIONS,
  buildAccountClipboardPayload,
} from './userManagementCoreUtils';
import {
  normalizeStringArray,
  INTERNAL_CONTROL_MODE_OPTIONS,
  INTERNAL_DRAFT_EVAL_PERMISSION_OPTIONS,
} from './userManagementTempScopeUtils';
import {
  runClipboardCopyWithMessage,
  clearUiMessage,
  setMessageWithAutoClear,
} from './userManagementActionUtils';
import {
  loadUserManagementOrgTree,
  buildOrgDeptOptionState,
  buildDeptCascadeOptions,
} from './userManagementOrgTreeUtils';
import {
  buildFinanceTempSeedPayload,
  buildFinanceTempCredentialExportRows,
  buildTempScopeChangeLogExportRows,
  buildTimestampedExportFilename,
  FINANCE_TEMP_AUDIT_ROLE_OPTIONS,
  buildFinanceOrgTreeHintMessage,
} from './userManagementFinanceTempUtils';
import {
  buildTempScopeChangeLogQuery,
  normalizeTempScopeChangeLogItems,
} from './userManagementChangeLogUtils';
import {
  buildTempScopeStateMaps,
  buildTempConfigSavePayload,
} from './userManagementTempScopeStateUtils';
import { runUserModalMutationFlow } from './userManagementModalFlowUtils';
import {
  buildAvatarTypeErrorMessage,
  buildAvatarSizeErrorMessage,
  buildProfileUpdateMessage,
  resolvePasswordPrecheckError,
  buildPasswordChangeMessage,
  buildPasswordResetFields,
} from './userManagementProfileUtils';
import {
  createEmptyBatchUserRow,
  buildAddedBatchUserRows,
  buildRemovedBatchUserRows,
  buildUpdatedBatchUserRows,
  buildBatchCreateValidationResult,
  buildBatchCreateErrorText,
  mapBatchRowToCreateApiPayload,
} from './userManagementBatchCreateUtils';
import {
  validateCreateUserForm,
  buildUserUpdatePayload,
  validateEditUserForm,
  validateResetPasswordForm,
} from './userManagementFormUtils';
import {
  BATCH_CREATE_ROLE_OPTIONS,
  CREATE_USER_ROLE_OPTIONS,
  getEditUserRoleOptions,
  normalizePlatformRoleForEdit,
} from './userManagementRoleOptionUtils';
import {
  filterUsersBySearch,
  isFinanceTempAccountUser,
} from './userManagementUserFilterUtils';
import {
  buildUserRoleTagView,
  buildUserOnlineStatusView,
  formatUserLastActive,
} from './userManagementUserRowUtils';
import {
  createEmptyFinanceTempRow,
  buildUpdatedFinanceTempRows,
  buildNextTempAuditDeptPath,
  resolveFinalDeptFromPath,
  toggleDeptSelection,
  addDeptSelections,
  toggleStringSelection,
  buildTempUserCardViewState,
} from './userManagementDeptStateUtils';
import {
  resolveTemplateIdForUser,
  getSelectedBatchUserIds,
  normalizeTemplatePreviewRows,
  buildTemplateSelectOptions,
  buildTemplateSelectPlaceholder,
  buildNextSelectedBatchUserIds,
  runTemplateActionWithLoading,
  validateSingleTemplateAction,
  validateBatchTemplateAction,
} from './userManagementTemplateFlowUtils';
import {
  buildDeleteUserConfirmMessage,
  buildUserDeletedSuccessMessage,
  buildUserRoleUpdatedSuccessMessage,
  buildUserSeedSuccessMessage,
  buildUserSeedFailedMessage,
  buildUserSeedExceptionMessage,
  buildSaveFailedMessage,
  buildTempConfigAuditGroupRequiredMessage,
  buildTempConfigSaveFailedMessage,
  buildTempConfigSaveSuccessMessage,
  buildTempConfigSaveSuccessAlertMessage,
  buildApplyTemplateSelectFirstMessage,
  buildPreviewTemplateSelectFirstMessage,
  buildPreviewTemplateSelectUsersFirstMessage,
  buildApplyTemplateSelectUsersFirstMessage,
  buildBatchTemplateSelectFirstMessage,
  buildBatchCreateUsersSuccessMessage,
  buildCreateUserSuccessMessage,
  buildUpdateUserSuccessMessage,
  buildResetUserPasswordSuccessMessage,
} from './userManagementNoticeUtils';

export { MODULE_ID_TO_SHORTNAME, MODULE_OPERATION_TYPES, AUDIT_ROLES, AUDIT_GROUPS, getAuditRolePermissionSummary, getAuditRoleOperationSummary, getModuleOperationsForUser, canModuleOperation, getModelLibraryAllowedModelIds };
export const getAuditGroups = () => AUDIT_GROUPS;
export const getAuditRoles = () => AUDIT_ROLES;
export { getUserAuditGroup, isGroupLeader, getGroupMembers, getUsers };
const OPERATION_NAME_MAP = MODULE_OPERATION_TYPES.reduce((acc, item) => {
  acc[item.id] = item.name;
  return acc;
}, {});
const USER_GROUP_RULES_STORAGE_KEY = 'user_management_group_rules_v1';
/** 列表分组仅用于展示排序；不再按审计角色/权限依据匹配（权限一律按用户模块勾选） */
const DEFAULT_USER_GROUP_RULES = [
  {
    id: 'platform-admin',
    label: '平台管理员组',
    hint: '系统管理员账号',
    className: 'bg-rose-50 text-rose-700 border-rose-200',
    platformRole: 'admin',
    onlyTemp: false,
    catchAll: false,
  },
  {
    id: 'temp',
    label: '临时账号组',
    hint: '财务临时等临时授权账号',
    className: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    platformRole: 'any',
    onlyTemp: true,
    catchAll: false,
  },
  {
    id: 'other',
    label: '其他账号组',
    hint: '其余门户账号',
    className: 'bg-slate-50 text-slate-700 border-slate-200',
    platformRole: 'any',
    onlyTemp: false,
    catchAll: true,
  },
];

function loadUserGroupRules() {
  try {
    const raw = localStorage.getItem(USER_GROUP_RULES_STORAGE_KEY);
    if (!raw) return DEFAULT_USER_GROUP_RULES;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_USER_GROUP_RULES;
    return parsed.map((rule, index) => ({
      id: String(rule?.id || `group-rule-${index + 1}`),
      label: String(rule?.label || `分组${index + 1}`),
      hint: String(rule?.hint || ''),
      className: String(rule?.className || 'bg-slate-50 text-slate-700 border-slate-200'),
      platformRole: ['any', 'admin', 'non-admin'].includes(rule?.platformRole) ? rule.platformRole : 'any',
      onlyTemp: Boolean(rule?.onlyTemp),
      catchAll: Boolean(rule?.catchAll),
    }));
  } catch {
    return DEFAULT_USER_GROUP_RULES;
  }
}

// 用户上下文
const UserContext = createContext(null);

export const MockUserProvider = ({ children, user: overrideUser = null }) => {
  const mockUser = overrideUser || {
    id: 'ui-capture-admin',
    username: 'ui_capture_admin',
    name: '截图测试管理员',
    email: 'ui-capture@audit.local',
    role: 'admin',
    auditRole: 'audit-director',
    auditGroup: 'audit',
    permissions: ['*'],
    mustChangePassword: false,
  };

  const noopSuccess = async () => ({ success: true });
  const value = {
    user: mockUser,
    loading: false,
    login: noopSuccess,
    logout: () => {},
    register: noopSuccess,
    getAllUsers: async () => [mockUser],
    adminCreateUser: noopSuccess,
    adminBatchCreateUsers: noopSuccess,
    adminSeedFinanceTempUsers: noopSuccess,
    getTempScopeTemplates: async () => ({ success: true, templates: [] }),
    adminApplyTempScopeTemplate: noopSuccess,
    previewTempScopeTemplate: async () => ({ success: true, items: [] }),
    getTempScopeChangeLogs: async () => ({ success: true, items: [] }),
    adminUpdateUser: noopSuccess,
    deleteUser: noopSuccess,
    resetUserPassword: noopSuccess,
    updateUser: noopSuccess,
    changePassword: noopSuccess,
    refreshMe: noopSuccess,
    serverUsersVersion: 1,
    isAdmin: true,
    isExecutive: true,
    isLoggedIn: true,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

/** 可选用户上下文：不在 Provider 内时返回 null，不抛错（用于门户等可能独立挂载的场景） */
export const useUserOptional = () => useContext(UserContext);


// User Provider
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverUsersVersion, setServerUsersVersion] = useState(0);

  // 获取 Auth Header
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // 飞书 OAuth 回跳：去掉 URL 参数并记下提示（个人中心弹窗展示）
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const fb = params.get('feishu_bind');
    if (!fb) return;
    const reason = params.get('feishu_bind_reason') || '';
    const u = new URL(window.location.href);
    u.searchParams.delete('feishu_bind');
    u.searchParams.delete('feishu_bind_reason');
    window.history.replaceState({}, '', u.pathname + u.search + u.hash);
    if (fb === 'success') {
      sessionStorage.setItem('audit_portal_feishu_bind_notice', JSON.stringify({ type: 'success' }));
    } else {
      sessionStorage.setItem('audit_portal_feishu_bind_notice', JSON.stringify({ type: 'error', reason }));
    }
  }, []);

  // 飞书 OAuth 弹窗：子窗口通过 postMessage 通知绑定结果
  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data;
      if (!d || d.type !== 'AUDIT_PORTAL_FEISHU_BIND') return;
      if (d.status === 'success') {
        sessionStorage.setItem('audit_portal_feishu_bind_notice', JSON.stringify({ type: 'success' }));
        const token = localStorage.getItem('token');
        if (!token) return;
        fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((userData) => {
            if (!userData) return;
            setUser((prev) => {
              const merged = {
                ...prev,
                ...userData,
                mustChangePassword: Boolean(userData?.mustChangePassword ?? prev?.mustChangePassword),
              };
              try {
                localStorage.setItem('audit_portal_user', JSON.stringify(merged));
              } catch (_) {}
              return merged;
            });
          })
          .catch(() => {});
      } else {
        sessionStorage.setItem(
          'audit_portal_feishu_bind_notice',
          JSON.stringify({ type: 'error', reason: d.reason || '' })
        );
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // 初始化：?demo=1 可走演示会话；否则检查 token，并同步用户列表到 localStorage
  useEffect(() => {
    let cancelled = false;

    const demoRequested = () => {
      if (typeof window === 'undefined') return false;
      const v = new URLSearchParams(window.location.search).get('demo');
      return v === '1' || v === 'true';
    };

    const stripDemoParam = () => {
      try {
        const u = new URL(window.location.href);
        if (!u.searchParams.has('demo')) return;
        u.searchParams.delete('demo');
        window.history.replaceState({}, '', u.pathname + u.search + u.hash);
      } catch (_) {}
    };

    const initAuth = async () => {
      if (demoRequested()) {
        try {
          const res = await fetch('/api/auth/demo', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: '{}',
          });
          let data = {};
          try {
            data = await res.json();
          } catch (_) {}
          if (cancelled) return;
          if (res.ok && data.token && data.user) {
            localStorage.setItem('token', data.token);
            const nextUser = {
              ...data.user,
              mustChangePassword: false,
              isDemoGuest: true,
            };
            setUser(nextUser);
            try {
              localStorage.setItem('audit_portal_user', JSON.stringify(nextUser));
            } catch (_) {}
            try {
              const usersRes = await fetch('/api/users', {
                headers: { Authorization: `Bearer ${data.token}` },
                credentials: 'include',
              });
              if (usersRes.ok) {
                const list = await usersRes.json();
                localStorage.setItem('audit_portal_users_db', JSON.stringify(list));
              }
            } catch (_) {}
            stripDemoParam();
            setLoading(false);
            return;
          }
          try {
            sessionStorage.setItem(
              'audit_portal_demo_error',
              data.error || `演示入口不可用（HTTP ${res.status}）`
            );
          } catch (_) {}
          localStorage.removeItem('token');
          localStorage.removeItem('audit_portal_user');
          setUser(null);
          stripDemoParam();
          setLoading(false);
          return;
        } catch (e) {
          console.error('Demo session failed:', e);
          if (!cancelled) {
            try {
              sessionStorage.setItem(
                'audit_portal_demo_error',
                '无法连接后端，请确认服务已启动且已在服务器开启演示入口（ALLOW_PUBLIC_DEMO）'
              );
            } catch (_) {}
            localStorage.removeItem('token');
            localStorage.removeItem('audit_portal_user');
            setUser(null);
            stripDemoParam();
            setLoading(false);
          }
          return;
        }
      }

      const token = localStorage.getItem('token');
      let cachedUser = null;
      try {
        cachedUser = JSON.parse(localStorage.getItem('audit_portal_user') || 'null');
      } catch (_) {}
      if (token) {
        try {
          const res = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
          });
          if (cancelled) return;
          if (res.ok) {
            const userData = await res.json();
            const mergedUser = {
              ...userData,
              mustChangePassword: Boolean(userData?.mustChangePassword || cachedUser?.mustChangePassword),
            };
            setUser(mergedUser);
            try {
              localStorage.setItem('audit_portal_user', JSON.stringify(mergedUser));
            } catch (_) {}
            try {
              const usersRes = await fetch('/api/users', {
                headers: { Authorization: `Bearer ${token}` },
                credentials: 'include',
              });
              if (usersRes.ok) {
                const list = await usersRes.json();
                localStorage.setItem('audit_portal_users_db', JSON.stringify(list));
              }
            } catch (_) {}
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('audit_portal_user');
            setUser(null);
          }
        } catch (e) {
          console.error('Auth check failed:', e);
          localStorage.removeItem('token');
          localStorage.removeItem('audit_portal_user');
          setUser(null);
        }
      }
      if (!cancelled) setLoading(false);
    };
    initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (username, password, opts = {}) => {
    const coldStartRetryStatuses = new Set([502, 503, 504]);
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    const maxAttempts = 4;
    const gapMs = 2500;

    try {
      let res;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (attempt > 0) await wait(gapMs);
        try {
          res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username,
              password,
              forceNewSession: !!opts.forceNewSession,
            }),
          });
        } catch (fetchErr) {
          if (attempt < maxAttempts - 1) continue;
          throw fetchErr;
        }
        if (!coldStartRetryStatuses.has(res.status)) break;
      }

      let data;
      try {
        data = await res.json();
      } catch (_) {
        return {
          success: false,
          error:
            res.status === 502
              ? '后端未启动或 Nginx 未反代到正确端口。请检查：1) pm2 已启动 audit-portal；2) /api 反代端口与 ecosystem/.env 中 PORT 一致（默认 3005）。若刚解压覆盖文件或执行了 pm2 restart，Node 需约 20～40 秒拉起子服务，请稍后刷新或再点登录。'
              : `请求异常(${res.status})，请检查网络与后端服务`
        };
      }
      if (res.status === 409 && (data?.code === 'SESSION_CONFLICT' || /其他终端/.test(String(data?.error || '')))) {
        return {
          success: false,
          error: data?.error || '该账号已在其他终端登录',
          code: 'SESSION_CONFLICT',
        };
      }

      if (!res.ok) return { success: false, error: data?.error || res.statusText || '登录失败' };

      localStorage.setItem('token', data.token);
      const nextUser = {
        ...(data.user || {}),
        mustChangePassword: Boolean(data?.mustChangePassword ?? data?.user?.mustChangePassword),
      };
      setUser(nextUser);
      try {
        localStorage.setItem('audit_portal_user', JSON.stringify(nextUser));
      } catch (_) {}
      addLog('login', 'user-management', {}).catch(() => {});
      return { success: true };
    } catch (e) {
      const msg = e?.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Load failed')) {
        return { success: false, error: '网络异常或后端未启动，请确认服务地址与端口' };
      }
      return { success: false, error: '登录请求失败' };
    }
  };

  const logout = () => {
    addLog('logout', 'user-management', {}).catch(() => {});
    try {
      sessionStorage.removeItem('audit_portal_demo_error');
    } catch (_) {}
    try {
      const t = localStorage.getItem('token');
      if (t) {
        fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${t}` },
          credentials: 'include',
        }).catch(() => {});
      }
    } catch (_) {}
    localStorage.removeItem('token');
    localStorage.removeItem('audit_portal_user');
    setUser(null);
  };

  const register = async (userData) => {
      // 注册功能暂未开放或仅限管理员，这里保留接口
      return { success: false, error: '暂不支持开放注册' };
  };

  // Admin functions：从服务器拉取用户列表，并同步写入 localStorage 供 getUsers() 等使用
  const getAllUsers = async () => {
      try {
          const res = await fetch('/api/users', { headers: getAuthHeader(), credentials: 'include' });
          if (res.ok) {
              const data = await res.json();
              try {
                  localStorage.setItem('audit_portal_users_db', JSON.stringify(data));
              } catch (_) {}
              return data;
          }
          return [];
      } catch (e) {
          console.error(e);
          return [];
      }
  };

  const adminCreateUser = async (data) => {
      try {
          const res = await fetch('/api/users', {
              method: 'POST',
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify(data)
          });
          const json = await res.json();
          if (res.ok) {
              setServerUsersVersion(v => v + 1);
              return { success: true, user: json };
          }
          return { success: false, error: json.error };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const adminBatchCreateUsers = async (usersList) => {
      let count = 0;
      let errors = [];
      for (const u of usersList) {
          const res = await adminCreateUser(u);
          if (res.success) count++;
          else errors.push(`${u.username}: ${res.error}`);
      }
      if (errors.length > 0) return { success: false, error: errors.join('\n'), count };
      return { success: true, count };
  };

  const adminSeedFinanceTempUsers = async (accounts) => {
      try {
          const res = await fetch('/api/users/seed-finance-temp', {
              method: 'POST',
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify({ accounts }),
              credentials: 'include'
          });
          const json = await res.json().catch(() => ({}));
          if (res.ok) {
              setServerUsersVersion(v => v + 1);
              return { success: true, data: json };
          }
          return { success: false, error: json.error || '创建失败' };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const getTempScopeTemplates = async () => {
      try {
          const res = await fetch('/api/users/temp-scope-templates', {
              headers: { ...getAuthHeader() },
              credentials: 'include'
          });
          const json = await res.json().catch(() => ({}));
          if (res.ok) return { success: true, templates: json.templates || [] };
          return { success: false, error: json.error || '获取模板失败' };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const adminApplyTempScopeTemplate = async (userIds, templateId) => {
      try {
          const res = await fetch('/api/users/apply-temp-scope-template', {
              method: 'POST',
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify({ userIds, templateId }),
              credentials: 'include'
          });
          const json = await res.json().catch(() => ({}));
          if (res.ok) {
              setServerUsersVersion(v => v + 1);
              return { success: true, data: json };
          }
          return { success: false, error: json.error || '应用模板失败' };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const previewTempScopeTemplate = async (userIds, templateId) => {
      try {
          const res = await fetch('/api/users/preview-temp-scope-template', {
              method: 'POST',
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify({ userIds, templateId }),
              credentials: 'include'
          });
          const json = await res.json().catch(() => ({}));
          if (res.ok) return { success: true, items: json.items || [] };
          return { success: false, error: json.error || '预览失败' };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const getTempScopeChangeLogs = async (params = {}) => {
      try {
          const q = new URLSearchParams();
          q.set('limit', String(params.limit || 100));
          if (params.targetUsername) q.set('targetUsername', String(params.targetUsername));
          if (params.templateId) q.set('templateId', String(params.templateId));
          if (params.startDate) q.set('startDate', String(params.startDate));
          if (params.endDate) q.set('endDate', String(params.endDate));
          const res = await fetch(`/api/users/temp-scope-change-logs?${q.toString()}`, {
              headers: { ...getAuthHeader() },
              credentials: 'include'
          });
          const json = await res.json().catch(() => ({}));
          if (res.ok) return { success: true, items: json.items || [] };
          return { success: false, error: json.error || '获取日志失败' };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const adminUpdateUser = async (id, updates) => {
      try {
          const res = await fetch(`/api/users/${id}`, {
              method: 'PUT',
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify(updates)
          });
          const json = await res.json();
          if (res.ok) {
              setServerUsersVersion(v => v + 1);
              // 如果更新的是自己，同步更新本地状态
              if (user && user.id === id) {
                  setUser(prev => ({ ...prev, ...json }));
              }
              return { success: true, auditRoleUnavailable: json.auditRoleUnavailable };
          }
          return { success: false, error: json.error };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const deleteUser = async (id) => {
      try {
          const res = await fetch(`/api/users/${id}`, {
              method: 'DELETE',
              headers: getAuthHeader()
          });
          if (res.ok) {
              setServerUsersVersion(v => v + 1);
              return { success: true };
          }
          const json = await res.json();
          return { success: false, error: json.error };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };
  
  const resetUserPassword = async (id, newPassword) => {
      return adminUpdateUser(id, { password: newPassword });
  };

  // Self update
  const updateUser = async (updates) => {
      if (!user) return { success: false, error: '未登录' };
      try {
          const res = await fetch(`/api/users/${user.id}`, {
              method: 'PUT',
              headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
              body: JSON.stringify(updates)
          });
          const json = await res.json();
          if (res.ok) {
              setUser(prev => {
                const merged = { ...prev, ...json };
                try { localStorage.setItem('audit_portal_user', JSON.stringify(merged)); } catch (_) {}
                return merged;
              });
              return { success: true };
          }
          return { success: false, error: json.error };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const refreshMe = async () => {
    if (!user) return { success: false, error: '未登录' };
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/auth/me', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) return { success: false, error: json.error || '刷新失败' };
      setUser((prev) => {
        const merged = {
          ...prev,
          ...json,
          mustChangePassword: Boolean(json?.mustChangePassword ?? prev?.mustChangePassword),
        };
        try {
          localStorage.setItem('audit_portal_user', JSON.stringify(merged));
        } catch (_) {}
        return merged;
      });
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.message || '刷新失败' };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    if (!user) return { success: false, error: '未登录' };
      const policyError = validatePasswordPolicy(newPassword, user.username);
      if (policyError) return { success: false, error: policyError };
      try {
        const res = await fetch('/api/auth/change-password', {
          method: 'POST',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ currentPassword: oldPassword, newPassword }),
          credentials: 'include'
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
          setUser(prev => {
            const merged = { ...prev, mustChangePassword: false };
            try { localStorage.setItem('audit_portal_user', JSON.stringify(merged)); } catch (_) {}
            return merged;
          });
          return { success: true };
        }
        return { success: false, error: json.error || '修改密码失败' };
      } catch (e) {
        return { success: false, error: e.message || '修改密码失败' };
      }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    getAllUsers,
    adminCreateUser,
    adminBatchCreateUsers,
    adminSeedFinanceTempUsers,
    getTempScopeTemplates,
    adminApplyTempScopeTemplate,
    previewTempScopeTemplate,
    getTempScopeChangeLogs,
    adminUpdateUser,
    deleteUser,
    resetUserPassword,
    updateUser,
    changePassword,
    refreshMe,
    serverUsersVersion,
    isAdmin: user?.role === 'admin',
    isExecutive: user?.role === 'admin' || user?.role === 'ceo', // executive 角色标识
    isLoggedIn: !!user
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// 登录页面组件（UI 见 ./LoginPage.jsx）
export function LoginPage({ onSuccess }) {
  const { login } = useUser();
  return <AuditPortalLoginPage login={login} onSuccess={onSuccess} />;
}

// 用户菜单组件
export function UserMenu() {
  const { user, logout, isAdmin } = useUser();
  const [showMenu, setShowMenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSwitchAccount, setShowSwitchAccount] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRef = React.useRef(null);
  const { isMobile } = useBreakpoint();

  if (!user) return null;

  const handleToggleMenu = () => {
    if (!showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: Math.max(8, window.innerWidth - rect.right)
      });
    }
    setShowMenu(!showMenu);
  };

  // 手机端：点击头像直接打开个人设置（修改个人信息和头像）
  const handleAvatarClick = () => {
    if (isMobile) {
      setShowProfile(true);
      setShowMenu(false);
    } else {
      handleToggleMenu();
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleAvatarClick}
        className="flex flex-col items-center gap-0.5 sm:flex-row sm:gap-2 px-2 py-1.5 sm:px-3 rounded-lg bg-white hover:bg-gray-50 transition-colors min-h-[44px] sm:min-h-0 border border-gray-200/80 shadow-sm"
        title={isMobile ? '个人设置' : ''}
      >
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-sm shrink-0">
          {user.avatar ? (
            <img src={user.avatar} alt="头像" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-medium text-sm">
              {user.name?.[0] || user.username[0].toUpperCase()}
            </span>
          )}
        </div>
        <span className="text-gray-800 text-sm hidden sm:block font-medium">{user.name || user.username}</span>
        {isMobile && <span className="text-[10px] text-gray-500 mt-0.5">我的</span>}
      </button>

      {showMenu && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setShowMenu(false)} />
          <div 
            className="fixed w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-[9999]"
            style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
          >
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="font-medium text-gray-800">{user.name || user.username}</p>
              <p className="text-xs text-gray-500">{user.email}</p>
              {isAdmin && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs rounded-full">
                  管理员
                </span>
              )}
            </div>
            <button
              onClick={() => { setShowProfile(true); setShowMenu(false); }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              个人中心
            </button>
            <button
              onClick={() => { logout(); setShowMenu(false); }}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              切换账号 / 退出登录
            </button>
          </div>
        </>
      )}

      {showProfile && <UserProfileModal onClose={() => setShowProfile(false)} />}
    </>
  );
}

// 用户资料编辑弹窗
function UserProfileModal({ onClose }) {
  const { user, updateUser, changePassword, refreshMe } = useUser();
  const [tab, setTab] = useState('profile'); 
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [avatar, setAvatar] = useState(user?.avatar || null);
  const [feishuBusy, setFeishuBusy] = useState(false);
  const [feishuChallengeCode, setFeishuChallengeCode] = useState('');
  const [feishuPolicyUploadChallengeCode, setFeishuPolicyUploadChallengeCode] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = React.useRef(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: buildAvatarTypeErrorMessage() });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: buildAvatarSizeErrorMessage() });
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxSize = 200;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        setAvatar(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async () => {
    const result = await updateUser({ name, email, avatar });
    setMessage(buildProfileUpdateMessage(result));
  };

  const maskFeishuOpenId = (oid) => {
    const s = String(oid || '').trim();
    if (!s) return '';
    if (s.length <= 12) return `${s.slice(0, 4)}…`;
    return `${s.slice(0, 6)}…${s.slice(-4)}`;
  };

  const mapFeishuOAuthReason = (reason) => {
    const r = String(reason || '');
    const map = {
      missing_params: '授权参数不完整，请重试',
      invalid_state: '授权已过期或无效，请重新点击绑定',
      no_database: '服务器未连接数据库，无法保存绑定',
      feishu_not_configured: '管理员尚未配置或未启用审计飞书机器人',
      token_exchange: '飞书拒绝授权或换票失败，请确认应用重定向 URL 与后台一致',
      user_info: '无法读取飞书用户信息，请检查应用权限',
      open_id_conflict: '该飞书账号已绑定其他门户用户',
      db_not_migrated: '数据库未迁移飞书字段，请联系管理员执行迁移',
      db_error: '保存绑定失败',
      server: '服务器处理异常',
    };
    return map[r] || (r ? `绑定失败（${r}）` : '绑定失败');
  };

  const handleFeishuOAuthStart = async () => {
    setFeishuBusy(true);
    clearUiMessage(setMessage);
    try {
      const token = localStorage.getItem('token');
      const ro = encodeURIComponent(window.location.origin);
      const res = await fetch(`/api/users/me/feishu-bind/oauth-url?popup=1&returnOrigin=${ro}`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.authorizeUrl) {
        setMessage({ type: 'error', text: j.error || '获取飞书授权地址失败' });
        return;
      }
      const ru = String(j.redirectUri || '');
      if (ru.startsWith('http://') && window.location.protocol === 'https:') {
        setMessage({
          type: 'error',
          text:
            `服务端生成的回调地址为「${ru}」（http），与飞书后台常见的 https 不一致时会报 20029。请在服务器 .env 设置 PUBLIC_API_BASE_URL=https://你的域名（无路径），或 FORCE_PUBLIC_HTTPS=1；或在审计设置「固定 OAuth 重定向地址」粘贴与飞书完全一致的一行并保存。`,
        });
        return;
      }
      const feat = 'width=560,height=720,scrollbars=yes,resizable=yes,status=yes';
      const win = window.open(j.authorizeUrl, 'audit_feishu_oauth', feat);
      if (!win || typeof win.closed === 'undefined') {
        setMessage({ type: 'error', text: '无法打开新窗口，请检查浏览器是否拦截了弹出式窗口' });
        return;
      }
      if (win.closed) {
        setMessage({ type: 'error', text: '弹出窗口已被关闭，请允许本站弹出窗口后重试' });
        return;
      }
      win.focus();
      const srcHint =
        j.redirectUriSource === 'inferred_from_request'
          ? ` 当前回调由请求头推断：${ru}（请与飞书后台逐字对照；若不对请设 PUBLIC_API_BASE_URL 或固定 OAuth 地址）。`
          : '';
      setMessage({
        type: 'success',
        text: `已打开飞书授权窗口。完成后请等待小窗自动关闭。${srcHint}`,
      });
    } finally {
      setFeishuBusy(false);
    }
  };

  const handleFeishuUnbind = async () => {
    setFeishuBusy(true);
    clearUiMessage(setMessage);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/me/feishu-bind', {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage({ type: 'error', text: j.error || '解绑失败' });
        return;
      }
      const r = await refreshMe();
      setMessage(
        r.success
          ? { type: 'success', text: '已解除飞书绑定' }
          : { type: 'success', text: '已解绑，请重新打开个人中心同步状态' }
      );
    } finally {
      setFeishuBusy(false);
    }
  };

  const handleFeishuRefresh = async () => {
    setFeishuBusy(true);
    clearUiMessage(setMessage);
    try {
      const r = await refreshMe();
      setMessage(
        r.success
          ? { type: 'success', text: '已同步账号信息' }
          : { type: 'error', text: r.error || '刷新失败' }
      );
    } finally {
      setFeishuBusy(false);
    }
  };

  const handleFeishuChallenge = async () => {
    setFeishuBusy(true);
    clearUiMessage(setMessage);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/me/feishu-bind/challenge', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: '{}',
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.code) {
        setFeishuChallengeCode('');
        setMessage({ type: 'error', text: j.error || '生成验证码失败' });
        return;
      }
      const codeStr = String(j.code);
      const bindLine = `绑定 ${codeStr}`;
      let copied = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(bindLine);
          copied = true;
        }
      } catch (_) {
        /* 非 HTTPS、权限或部分浏览器在异步后收紧用户手势时可能失败 */
      }
      if (!copied) {
        try {
          const ta = document.createElement('textarea');
          ta.value = bindLine;
          ta.setAttribute('readonly', '');
          ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
          document.body.appendChild(ta);
          ta.select();
          copied = document.execCommand('copy');
          document.body.removeChild(ta);
        } catch (_) {
          /* ignore */
        }
      }
      setFeishuChallengeCode(codeStr);
      setMessage({
        type: 'success',
        text: copied
          ? '验证码已生成，整行「绑定 …」已复制到剪贴板。15 分钟内有效，打开飞书与审计机器人单聊直接粘贴发送即可。'
          : '验证码已生成，15 分钟内有效。浏览器未允许自动复制，请手动复制下方整行到飞书单聊发送。',
      });
    } finally {
      setFeishuBusy(false);
    }
  };

  const handleFeishuPolicyUploadChallenge = async () => {
    setFeishuBusy(true);
    clearUiMessage(setMessage);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/me/feishu-policy-upload/challenge', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: '{}',
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.code) {
        setFeishuPolicyUploadChallengeCode('');
        setMessage({ type: 'error', text: j.error || '生成验证码失败' });
        return;
      }
      const codeStr = String(j.code);
      const line = `制度验证 ${codeStr}`;
      let copied = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(line);
          copied = true;
        }
      } catch (_) {
        /* ignore */
      }
      if (!copied) {
        try {
          const ta = document.createElement('textarea');
          ta.value = line;
          ta.setAttribute('readonly', '');
          ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
          document.body.appendChild(ta);
          ta.select();
          copied = document.execCommand('copy');
          document.body.removeChild(ta);
        } catch (_) {
          /* ignore */
        }
      }
      setFeishuPolicyUploadChallengeCode(codeStr);
      setMessage({
        type: 'success',
        text: copied
          ? '制度上传验证码已生成，整行已复制。15 分钟内有效；在飞书审计机器人单聊粘贴发送完成验证后，后续上传制度文件无需再次验证。'
          : '验证码已生成，请手动复制下方整行到飞书单聊发送。',
      });
    } finally {
      setFeishuBusy(false);
    }
  };

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('audit_portal_feishu_bind_notice');
      if (!raw) return;
      sessionStorage.removeItem('audit_portal_feishu_bind_notice');
      const j = JSON.parse(raw);
      if (j.type === 'success') {
        setMessage({ type: 'success', text: '飞书账号已通过授权完成绑定' });
      } else {
        setMessage({ type: 'error', text: mapFeishuOAuthReason(j.reason) });
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 弹窗挂载时消费一次 OAuth 回跳提示
  }, []);

  const handleChangePassword = async () => {
    const precheckError = resolvePasswordPrecheckError({
      newPassword,
      confirmPassword,
      username: user?.username,
      validatePasswordPolicyFn: validatePasswordPolicy,
    });
    if (precheckError) {
      setMessage({ type: 'error', text: precheckError });
      return;
    }
    const result = await changePassword(oldPassword, newPassword);
    setMessage(buildPasswordChangeMessage(result));
    if (result.success) {
      const resetFields = buildPasswordResetFields();
      setOldPassword(resetFields.oldPassword);
      setNewPassword(resetFields.newPassword);
      setConfirmPassword(resetFields.confirmPassword);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">个人设置</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => { setTab('profile'); clearUiMessage(setMessage); }}
            className={`flex-1 py-3 text-sm font-medium ${tab === 'profile' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500'}`}
          >
            基本资料
          </button>
          <button
            onClick={() => { setTab('password'); clearUiMessage(setMessage); }}
            className={`flex-1 py-3 text-sm font-medium ${tab === 'password' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500'}`}
          >
            修改密码
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {message.text && (
            <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'
            }`}>
              {message.type === 'success' ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {message.text}
            </div>
          )}

          {tab === 'profile' ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center mb-4">
                <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-lg">
                    {avatar ? (
                      <img src={avatar} alt="头像" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-3xl font-bold">
                        {user?.name?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                  <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                <p className="text-xs text-gray-500 mt-2">点击更换头像</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                <input type="text" value={user?.username} disabled className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">姓名</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>

              <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-slate-50/90">
                <div className="text-sm font-medium text-gray-800">飞书审计机器人</div>
                {user?.feishuOpenId ? (
                  <>
                    <p className="text-xs text-gray-600">
                      已绑定飞书账号（open_id）：{' '}
                      <span className="font-mono text-gray-800">{maskFeishuOpenId(user.feishuOpenId)}</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={feishuBusy}
                        onClick={handleFeishuRefresh}
                        className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        刷新状态
                      </button>
                      <button
                        type="button"
                        disabled={feishuBusy}
                        onClick={handleFeishuUnbind}
                        className="px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                      >
                        解绑飞书
                      </button>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
                      <div className="text-xs font-medium text-gray-800">制度库 · 飞书单聊上传验证</div>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        向机器人<strong>上传制度附件</strong>前，若管理员启用了验证：请先生成本页验证码，在单聊发送{' '}
                        <code className="bg-white px-1 rounded text-[11px]">制度验证 验证码</code>
                        ，再发「制度上传目录」选定云盘文件夹后上传文件。
                      </p>
                      {user?.feishuPolicyUploadVerifiedUntil &&
                      new Date(user.feishuPolicyUploadVerifiedUntil) > new Date() ? (
                        <p className="text-xs text-emerald-700">
                          {new Date(user.feishuPolicyUploadVerifiedUntil).getFullYear() >= 2090
                            ? '已通过制度库单聊上传身份验证（长期有效）。'
                            : `当前验证有效至：${new Date(user.feishuPolicyUploadVerifiedUntil).toLocaleString('zh-CN', {
                                hour12: false,
                              })}`}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">当前无有效上传验证（解绑飞书会清除；可点刷新状态同步）。</p>
                      )}
                      <button
                        type="button"
                        disabled={feishuBusy}
                        onClick={handleFeishuPolicyUploadChallenge}
                        className="px-3 py-2 text-sm rounded-lg border border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                      >
                        生成制度库上传验证码
                      </button>
                      {feishuPolicyUploadChallengeCode ? (
                        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 space-y-1">
                          <div className="text-xs text-gray-500">飞书单聊中粘贴发送（已尝试自动复制整行）：</div>
                          <div className="font-mono text-sm text-gray-900 break-all select-all">
                            制度验证 {feishuPolicyUploadChallengeCode}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      每位用户需<strong>单独绑定</strong>自己的飞书身份后，机器人才会按您的门户账号权限响应指令。
                      可选：<strong>飞书授权</strong>（新窗口 OAuth）或<strong>验证码</strong>（门户生成 12 位码，在机器人单聊发送「绑定 验证码」）。
                    </p>
                    <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
                      若飞书提示重定向 URL 错误（20029）：请管理员在审计设置中复制 OAuth 回调地址，粘贴到飞书后台「安全设置 → 重定向 URL」，并在「固定 OAuth 重定向地址」中填写<strong>完全相同</strong>的一行后保存。
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={feishuBusy}
                        onClick={handleFeishuOAuthStart}
                        className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        在新窗口打开飞书授权
                      </button>
                      <button
                        type="button"
                        disabled={feishuBusy}
                        onClick={handleFeishuChallenge}
                        className="px-3 py-2 text-sm rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 disabled:opacity-50"
                      >
                        生成 12 位验证码
                      </button>
                      <button
                        type="button"
                        disabled={feishuBusy}
                        onClick={handleFeishuRefresh}
                        className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        刷新状态
                      </button>
                    </div>
                    {feishuChallengeCode ? (
                      <div className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2 space-y-1">
                        <div className="text-xs text-gray-500">飞书单聊中粘贴发送（已尝试自动复制整行）：</div>
                        <div className="font-mono text-sm text-gray-900 break-all select-all">
                          绑定 {feishuChallengeCode}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <button onClick={handleUpdateProfile} className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                保存修改
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">原密码</label>
                <div className="relative">
                  <input type={showOldPassword ? "text" : "password"} value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 pr-10" />
                  <button type="button" onClick={() => setShowOldPassword(!showOldPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showOldPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                <div className="relative">
                  <input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 pr-10" />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
                <div className="relative">
                  <input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 pr-10" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button onClick={handleChangePassword} className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2">
                <Lock className="w-4 h-4" />
                修改密码
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// 批量新建用户弹窗
function BatchCreateUserModal({ onClose, onCreate }) {
  const [userList, setUserList] = useState([
    createEmptyBatchUserRow()
  ]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addUserRow = () => {
    setUserList((prev) => buildAddedBatchUserRows(prev));
  };

  const removeUserRow = (index) => {
    setUserList((prev) => buildRemovedBatchUserRows(prev, index, 1));
  };

  const updateUserRow = (index, field, value) => {
    setUserList((prev) => buildUpdatedBatchUserRows(prev, index, field, value));
  };

  const handleSubmit = async () => {
    const { errors, usersToCreate } = buildBatchCreateValidationResult(userList);

    if (errors.length > 0) {
      setError(buildBatchCreateErrorText(errors, 5));
      return;
    }
    if (usersToCreate.length === 0) {
      setError('请至少添加一个有效的用户');
      return;
    }

    setIsSubmitting(true);
    const payloads = usersToCreate.map(mapBatchRowToCreateApiPayload);
    const result = await onCreate(payloads);
    if (result && result.error) {
      setError(result.error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-xl">
          <h4 className="font-semibold text-lg flex items-center gap-2"><Users className="w-5 h-5" />批量新建用户</h4>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 flex-1 overflow-auto">
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm whitespace-pre-wrap">{error}</div>}
          <div className="border rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm text-left min-w-[720px]">
              <thead className="bg-gray-50 text-gray-700 font-medium">
                <tr>
                  <th className="px-4 py-3 w-12 text-center">#</th>
                  <th className="px-4 py-3 w-40">用户名 <span className="text-red-500">*</span></th>
                  <th className="px-4 py-3 w-40">姓名 <span className="text-red-500">*</span></th>
                  <th className="px-4 py-3 w-48">邮箱 <span className="text-red-500">*</span></th>
                  <th className="px-4 py-3 w-40">密码 <span className="text-red-500">*</span></th>
                  <th className="px-4 py-3 w-32">平台角色</th>
                  <th className="px-4 py-3 w-16 text-center">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {userList.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50 group">
                    <td className="px-4 py-2 text-center text-gray-500">{index + 1}</td>
                    <td className="px-4 py-2"><input type="text" value={user.username} onChange={(e) => updateUserRow(index, 'username', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="用户名" /></td>
                    <td className="px-4 py-2"><input type="text" value={user.name} onChange={(e) => updateUserRow(index, 'name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="姓名" /></td>
                    <td className="px-4 py-2"><input type="email" value={user.email} onChange={(e) => updateUserRow(index, 'email', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="邮箱" /></td>
                    <td className="px-4 py-2"><input type="text" value={user.password} onChange={(e) => updateUserRow(index, 'password', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="密码" /></td>
                    <td className="px-4 py-2">
                      <select value={user.role} onChange={(e) => updateUserRow(index, 'role', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                        {BATCH_CREATE_ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button onClick={() => removeUserRow(index)} className={`p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors ${userList.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={userList.length <= 1}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4">
            <button onClick={addUserRow} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors border border-dashed border-indigo-200 w-full justify-center"><Plus className="w-4 h-4" />添加一行</button>
          </div>
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white text-gray-700 font-medium">取消</button>
          <button onClick={handleSubmit} disabled={isSubmitting} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium shadow-sm flex items-center gap-2">{isSubmitting ? '提交中...' : '确认创建'}</button>
        </div>
      </div>
    </div>
  );
}

function UserGroupRuleConfigModal({ isOpen, onClose, rules, setRules, onSave, onReset, isAdmin }) {
  if (!isOpen) return null;
  const updateRule = (index, key, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, [key]: value } : rule)));
  };
  const moveRule = (index, direction) => {
    setRules((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-800">用户分组规则配置</h4>
            <p className="text-xs text-gray-500 mt-1">仅用于列表展示分组（平台角色 / 是否临时账号 / 兜底）。权限一律按用户模块勾选，不按审计角色或审计组匹配。</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-auto p-6 space-y-4 bg-gray-50/60">
          {rules.map((rule, index) => (
            <div key={`${rule.id}-${index}`} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">规则 #{index + 1}</span>
                  <span className={`text-xs px-2 py-0.5 rounded border ${rule.className}`}>{rule.label || '未命名分组'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" disabled={!isAdmin || index === 0} onClick={() => moveRule(index, -1)} className="px-2 py-1 text-xs rounded border border-gray-200 bg-white disabled:opacity-50">上移</button>
                  <button type="button" disabled={!isAdmin || index === rules.length - 1} onClick={() => moveRule(index, 1)} className="px-2 py-1 text-xs rounded border border-gray-200 bg-white disabled:opacity-50">下移</button>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">分组名称</div>
                  <input disabled={!isAdmin} value={rule.label || ''} onChange={(e) => updateRule(index, 'label', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:bg-gray-100" />
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">说明</div>
                  <input disabled={!isAdmin} value={rule.hint || ''} onChange={(e) => updateRule(index, 'hint', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:bg-gray-100" />
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">颜色样式</div>
                  <select disabled={!isAdmin} value={rule.className || ''} onChange={(e) => updateRule(index, 'className', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:bg-gray-100">
                    <option value="bg-rose-50 text-rose-700 border-rose-200">红色</option>
                    <option value="bg-violet-50 text-violet-700 border-violet-200">紫色</option>
                    <option value="bg-indigo-50 text-indigo-700 border-indigo-200">靛蓝</option>
                    <option value="bg-emerald-50 text-emerald-700 border-emerald-200">绿色</option>
                    <option value="bg-cyan-50 text-cyan-700 border-cyan-200">青色</option>
                    <option value="bg-slate-50 text-slate-700 border-slate-200">灰色</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] text-gray-500 mb-1">平台角色</div>
                  <select disabled={!isAdmin} value={rule.platformRole || 'any'} onChange={(e) => updateRule(index, 'platformRole', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:bg-gray-100">
                    <option value="any">不限</option>
                    <option value="admin">仅管理员</option>
                    <option value="non-admin">仅非管理员</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 lg:mt-6">
                  <input type="checkbox" disabled={!isAdmin} checked={!!rule.onlyTemp} onChange={(e) => updateRule(index, 'onlyTemp', e.target.checked)} className="w-4 h-4 rounded" />
                  仅临时账号
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 lg:mt-6">
                  <input type="checkbox" disabled={!isAdmin} checked={!!rule.catchAll} onChange={(e) => updateRule(index, 'catchAll', e.target.checked)} className="w-4 h-4 rounded" />
                  兜底分组
                </label>
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 bg-white flex items-center justify-between">
          <button type="button" disabled={!isAdmin} onClick={onReset} className="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 disabled:opacity-60">恢复默认</button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50">关闭</button>
            <button type="button" disabled={!isAdmin} onClick={onSave} className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">保存规则</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 管理员用户管理组件（embedded=true 时作为普通页面显示，否则为弹窗；hideHeader 用于嵌入统一用户管理系统时隐藏本页标题）
export function AdminUserManagement({ onClose, embedded = false, hideHeader = false }) {
  const {
    user,
    getAllUsers,
    deleteUser,
    adminUpdateUser,
    resetUserPassword,
    adminCreateUser,
    adminBatchCreateUsers,
    adminSeedFinanceTempUsers,
    getTempScopeTemplates,
    adminApplyTempScopeTemplate,
    previewTempScopeTemplate,
    getTempScopeChangeLogs,
    serverUsersVersion
  } = useUser();
  const [users, setUsers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('audit_portal_users_db') || '[]');
    } catch {
      return [];
    }
  });
  const [usersLoading, setUsersLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBatchCreateModal, setShowBatchCreateModal] = useState(false);
  const [showFinanceTempZone, setShowFinanceTempZone] = useState(false);
  const [showGroupRuleConfig, setShowGroupRuleConfig] = useState(false);
  const [showOnlyAnomalyUsers, setShowOnlyAnomalyUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [showAccountPasswordUser, setShowAccountPasswordUser] = useState(null);
  const [permissionDiagUserId, setPermissionDiagUserId] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [seeding, setSeeding] = useState(false);
  const [batchResetting, setBatchResetting] = useState(false);
  const [userGroupRules, setUserGroupRules] = useState(() => loadUserGroupRules());

  const handleCopyAccountCredential = useCallback((mode) => {
    if (!showAccountPasswordUser?.username) return;
    runClipboardCopyWithMessage({
      navigatorObj: navigator,
      username: showAccountPasswordUser.username,
      mode,
      buildPayload: buildAccountClipboardPayload,
      setMessage,
      clearDelayMs: 2000,
    });
  }, [showAccountPasswordUser, setMessage]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setLoadError('');
    try {
      const data = await getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadError('载入失败，请点击刷新重试');
      console.error('getAllUsers error:', e);
    } finally {
      setUsersLoading(false);
    }
  }, [getAllUsers]);

  const handleSeedPresetUsers = useCallback(async () => {
    if (seeding) return;
    setSeeding(true);
    clearUiMessage(setMessage);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/seed', {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessageWithAutoClear({
          setMessage,
          type: 'success',
          text: buildUserSeedSuccessMessage(data.created, data.message),
          clearDelayMs: 6000,
        });
        await loadUsers();
      } else {
        setMessageWithAutoClear({
          setMessage,
          type: 'error',
          text: buildUserSeedFailedMessage(data.error),
          clearDelayMs: 6000,
        });
      }
    } catch (e) {
      setMessageWithAutoClear({
        setMessage,
        type: 'error',
        text: buildUserSeedExceptionMessage(e.message),
        clearDelayMs: 6000,
      });
    } finally {
      setSeeding(false);
    }
  }, [seeding, loadUsers, setMessage]);

  const handleBatchResetPasswordsAndExport = useCallback(async () => {
    if (user?.role !== 'admin' || batchResetting) return;
    const confirmed = window.confirm('将批量重置用户密码并导出一次性初始密码。生成后目标用户将被强制首次登录修改密码。是否继续？');
    if (!confirmed) return;
    setBatchResetting(true);
    clearUiMessage(setMessage);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/users/batch-reset-passwords-export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        const rows = (Array.isArray(data.temporaryCredentials) ? data.temporaryCredentials : []).map((row, index) => ({
          序号: index + 1,
          用户名: row.username || '',
          姓名: row.name || '',
          角色: row.role || 'user',
          一次性初始密码: row.tempPassword || '',
          首次登录强制改密: '是',
        }));
        if (rows.length > 0) {
          const ws = XLSX.utils.json_to_sheet(rows);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, '一次性初始密码');
          XLSX.writeFile(wb, buildTimestampedExportFilename('批量重置密码'));
        }
        setMessageWithAutoClear({
          setMessage,
          type: 'success',
          text: data.message || `已批量重置 ${data.resetCount || rows.length || 0} 个用户密码，并导出一次性初始密码。`,
          clearDelayMs: 6000,
        });
        await loadUsers();
      } else {
        setMessageWithAutoClear({
          setMessage,
          type: 'error',
          text: data?.error || '批量重置失败',
          clearDelayMs: 6000,
        });
      }
    } catch (e) {
      setMessageWithAutoClear({
        setMessage,
        type: 'error',
        text: `批量重置失败：${e.message || '未知错误'}`,
        clearDelayMs: 6000,
      });
    } finally {
      setBatchResetting(false);
    }
  }, [user?.role, batchResetting, loadUsers, setMessage]);

  useEffect(() => {
    loadUsers();
  }, [serverUsersVersion]); // eslint-disable-line react-hooks/exhaustive-deps -- 仅随 serverUsersVersion 刷新，手动刷新用按钮

  const filteredUsers = useMemo(
    () => filterUsersBySearch(users, searchTerm),
    [users, searchTerm]
  );
  const [collapsedUserGroupMap, setCollapsedUserGroupMap] = useState({});

  const formatLastActive = useCallback((iso) => formatUserLastActive(iso), []);

  const buildPermissionBasisLabel = useCallback(() => '按用户配置', []);

  // 性能优化：权限可视化按用户列表一次性预计算，避免每张卡片重复遍历模块并多次读取配置
  const permissionOverviewByUserId = useMemo(() => {
    const map = new Map();
    (users || []).forEach((userItem) => {
      const userModuleIds = Array.from(new Set(Array.isArray(userItem?.permissions) ? userItem.permissions : []));
      const allKnownModuleIdSet = new Set(ALL_PERMISSION_MODULES.map((module) => module.id));
      const moduleNames = [];
      const visibleModuleIds = [];
      const opSet = new Set();
      ALL_PERMISSION_MODULES.forEach((module) => {
        const ops = getModuleOperationsForUser(userItem, module.id);
        if (!ops.includes('view')) return;
        visibleModuleIds.push(module.id);
        moduleNames.push(MODULE_ID_TO_SHORTNAME[module.id] || module.id);
        ops.forEach((op) => opSet.add(op));
      });
      const formatModuleLabel = (moduleId) => {
        const shortName = MODULE_ID_TO_SHORTNAME[moduleId] || moduleId;
        return shortName === moduleId ? moduleId : `${shortName}（${moduleId}）`;
      };
      const userModuleLabels = userModuleIds.map(formatModuleLabel);
      const visibleModuleLabels = visibleModuleIds.map(formatModuleLabel);
      const userButNotVisibleIds = userModuleIds.filter((moduleId) => allKnownModuleIdSet.has(moduleId) && !visibleModuleIds.includes(moduleId));
      const diagnosisText = [
        `用户：${userItem?.name || userItem?.username || userItem?.id || '-'}`,
        `用户ID：${userItem?.id || '-'}`,
        `权限依据：按用户配置（唯一策略）`,
        `用户管理已勾选模块（${userModuleLabels.length}）：${userModuleLabels.join('、') || '空'}`,
        `门户最终可见模块（${visibleModuleLabels.length}）：${visibleModuleLabels.join('、') || '空'}`,
        `已勾选但未可见（可能被模块策略或未配置功能权限挡住）（${userButNotVisibleIds.length}）：${userButNotVisibleIds.map(formatModuleLabel).join('、') || '无'}`,
      ].join('\n');
      const operationLabels = MODULE_OPERATION_TYPES
        .filter((item) => opSet.has(item.id))
        .map((item) => OPERATION_NAME_MAP[item.id] || item.name);
      map.set(userItem.id, {
        moduleNames,
        userModuleLabels,
        visibleModuleLabels,
        userButNotVisibleLabels: userButNotVisibleIds.map(formatModuleLabel),
        diagnosisText,
        operationLabels,
        permissionBasisLabel: buildPermissionBasisLabel(),
      });
    });
    return map;
  }, [users, buildPermissionBasisLabel]);

  const activePermissionDiag = useMemo(() => {
    if (!permissionDiagUserId) return null;
    const targetUser = (users || []).find((userItem) => userItem.id === permissionDiagUserId);
    if (!targetUser) return null;
    const overview = permissionOverviewByUserId.get(permissionDiagUserId);
    if (!overview) return null;
    return { user: targetUser, overview };
  }, [permissionDiagUserId, users, permissionOverviewByUserId]);

  const handleCopyPermissionDiag = useCallback(async () => {
    const text = activePermissionDiag?.overview?.diagnosisText;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setMessageWithAutoClear({
        setMessage,
        type: 'success',
        text: '权限一致性诊断已复制到剪贴板',
        clearDelayMs: 2500,
      });
    } catch (err) {
      setMessageWithAutoClear({
        setMessage,
        type: 'error',
        text: `复制失败：${err?.message || '请手动复制'}`,
        clearDelayMs: 3000,
      });
    }
  }, [activePermissionDiag, setMessage]);

  const usersAfterAnomalyFilter = useMemo(() => {
    if (!showOnlyAnomalyUsers) return filteredUsers;
    return filteredUsers;
  }, [filteredUsers, showOnlyAnomalyUsers]);

  const matchUserGroupRule = useCallback((rule, userItem) => {
    if (rule?.catchAll) return true;
    const isTemp = isFinanceTempAccountUser(userItem);
    if (rule?.onlyTemp && !isTemp) return false;
    if (rule?.platformRole === 'admin' && userItem?.role !== 'admin') return false;
    if (rule?.platformRole === 'non-admin' && userItem?.role === 'admin') return false;
    const hasAnyCondition = Boolean(
      rule?.onlyTemp ||
      (rule?.platformRole && rule.platformRole !== 'any')
    );
    return hasAnyCondition;
  }, []);

  const groupedUsersForDisplay = useMemo(() => {
    const list = (userGroupRules || []).map((tpl) => ({ ...tpl, users: [] }));
    usersAfterAnomalyFilter.forEach((u) => {
      const target = list.find((tpl) => matchUserGroupRule(tpl, u)) || list[list.length - 1];
      target.users.push(u);
    });
    return list.filter((item) => item.users.length > 0);
  }, [usersAfterAnomalyFilter, userGroupRules, matchUserGroupRule]);

  useEffect(() => {
    setCollapsedUserGroupMap((prev) => {
      const next = { ...prev };
      let hasChanged = false;
      groupedUsersForDisplay.forEach((groupItem) => {
        if (typeof next[groupItem.id] !== 'boolean') {
          next[groupItem.id] = false;
          hasChanged = true;
        }
      });
      return hasChanged ? next : prev;
    });
  }, [groupedUsersForDisplay]);

  const persistUserGroupRules = useCallback((nextRules) => {
    try {
      localStorage.setItem(USER_GROUP_RULES_STORAGE_KEY, JSON.stringify(nextRules));
    } catch (_) {}
  }, []);

  const handleDelete = async (userId, username) => {
    if (!confirm(buildDeleteUserConfirmMessage(username))) return;
    const result = await deleteUser(userId);
    if (result.success) {
      setMessageWithAutoClear({
        setMessage,
        type: 'success',
        text: buildUserDeletedSuccessMessage(),
        clearDelayMs: 3000,
      });
      loadUsers();
    } else {
      setMessageWithAutoClear({
        setMessage,
        type: 'error',
        text: result.error,
        clearDelayMs: 3000,
      });
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const result = await adminUpdateUser(userId, { role: newRole });
    if (result.success) {
      setMessageWithAutoClear({
        setMessage,
        type: 'success',
        text: buildUserRoleUpdatedSuccessMessage(),
        clearDelayMs: 3000,
      });
      loadUsers();
    } else {
      setMessageWithAutoClear({
        setMessage,
        type: 'error',
        text: result.error,
        clearDelayMs: 3000,
      });
    }
  };

  const toggleUserGroupCollapse = useCallback((groupId) => {
    setCollapsedUserGroupMap((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  const handleSaveUserGroupRules = useCallback(() => {
    persistUserGroupRules(userGroupRules);
    setShowGroupRuleConfig(false);
    setMessageWithAutoClear({
      setMessage,
      type: 'success',
      text: '分组规则已保存',
      clearDelayMs: 2500,
    });
  }, [persistUserGroupRules, userGroupRules, setMessage]);

  const handleResetUserGroupRules = useCallback(() => {
    setUserGroupRules(DEFAULT_USER_GROUP_RULES);
    persistUserGroupRules(DEFAULT_USER_GROUP_RULES);
    setMessageWithAutoClear({
      setMessage,
      type: 'success',
      text: '分组规则已恢复默认',
      clearDelayMs: 2500,
    });
  }, [persistUserGroupRules, setMessage]);

  const renderUserCard = (u) => {
    const roleView = buildUserRoleTagView(u.role);
    const onlineView = buildUserOnlineStatusView(u, user?.id, formatLastActive);
    const permissionOverview = permissionOverviewByUserId.get(u.id) || {
      moduleNames: [],
      operationLabels: [],
      permissionBasisLabel: buildPermissionBasisLabel(),
    };
    const previewModules = permissionOverview.moduleNames.slice(0, 8);
    const hiddenModuleCount = Math.max(0, permissionOverview.moduleNames.length - previewModules.length);
    return (
      <div key={u.id} className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm transition-all">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-600 font-bold text-xs border border-indigo-200 shrink-0">
              {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full rounded-full object-cover" /> : (u.name?.[0] || u.username[0]).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-gray-900 truncate">{u.name || u.username}</div>
              <div className="text-xs text-gray-500 truncate">{u.email || '—'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowAccountPasswordUser(u)} className="p-1.5 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors" title="查看账号密码"><Info className="w-4 h-4" /></button>
            <button onClick={() => setResetPasswordUser(u)} className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-colors" title="重置密码"><Key className="w-4 h-4" /></button>
            <button onClick={() => setEditingUser(u)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="编辑"><Edit2 className="w-4 h-4" /></button>
            <button onClick={() => handleDelete(u.id, u.username)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="删除"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div>
            <div className="text-[11px] text-gray-500 mb-1">平台角色</div>
            <button onClick={() => handleToggleRole(u.id, u.role)} className={`px-2 py-1 rounded-full text-xs font-medium border transition-colors ${roleView.className}`}>
              {roleView.label}
            </button>
          </div>
          <p className="text-[11px] text-gray-500 max-w-md leading-relaxed">
            专栏权限仅在下方「权限可视化」体现：请在展开模块权限区域勾选可访问模块，并在「权限管理 → 用户授权」配置操作粒度。
          </p>
        </div>

        <div className="mt-3 p-3 rounded-lg border border-indigo-100 bg-indigo-50/40">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs font-semibold text-indigo-700">
              权限可视化
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPermissionDiagUserId(u.id)}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-100 transition-colors"
                title="单用户诊断：已勾选模块与门户最终可见模块差异"
              >
                <Shield className="w-3.5 h-3.5" />
                一致性诊断
              </button>
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium border ${
                onlineView.isOnline
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${onlineView.isOnline ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                {onlineView.isOnline ? '在线' : '离线'}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {previewModules.map((name) => (
              <span key={`${u.id}-m-${name}`} className="px-2 py-0.5 rounded-md bg-white border border-indigo-200 text-[11px] text-indigo-700">
                {name}
              </span>
            ))}
            {hiddenModuleCount > 0 && (
              <span className="px-2 py-0.5 rounded-md bg-white border border-indigo-200 text-[11px] text-indigo-700">
                +{hiddenModuleCount} 个模块
              </span>
            )}
            {!permissionOverview.moduleNames.length && (
              <span className="text-[11px] text-gray-500">未分配模块</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {permissionOverview.operationLabels.map((label) => (
              <span key={`${u.id}-o-${label}`} className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-[11px] text-emerald-700">
                {label}
              </span>
            ))}
            {!permissionOverview.operationLabels.length && (
              <span className="text-[11px] text-gray-500">未配置操作权限</span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-gray-400">
            {onlineView.tip}{!onlineView.isOnline && onlineView.offlineAt ? ` · 最后在线：${onlineView.offlineAt}` : ''}
          </div>
        </div>
      </div>
    );
  };

  const header = (
    <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 shrink-0">
      <div className="flex items-center gap-3 text-white">
        {embedded && onClose && (
          <button type="button" onClick={onClose} className="flex items-center gap-1.5 px-2 py-1.5 -ml-1 rounded-lg hover:bg-white/20 text-white text-sm font-medium transition-colors">
            <ChevronLeft className="w-5 h-5" />
            返回
          </button>
        )}
        <Users className="w-6 h-6" />
        <h3 className="text-lg font-semibold">用户管理</h3>
        <span className="px-2 py-0.5 bg-white/20 rounded-full text-sm">{users.length} 用户</span>
      </div>
      {!embedded && (
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg text-white"><X className="w-5 h-5" /></button>
      )}
    </div>
  );

  const contentBoxClass = embedded
    ? 'bg-white rounded-xl shadow-sm flex-1 min-h-0 overflow-hidden flex flex-col'
    : 'bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col';

  if (showFinanceTempZone) {
    return (
      <div className={embedded ? 'h-full flex flex-col' : 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'}>
        <div className={contentBoxClass}>
          {!hideHeader && header}
          <FinanceTempAccountZoneModal
            embedded={true}
            canEdit={user?.role === 'admin'}
            users={users}
            onClose={() => setShowFinanceTempZone(false)}
            onSubmit={async (accounts) => {
              const result = await adminSeedFinanceTempUsers(accounts);
              if (!result.success) return { error: result.error || '创建失败' };
              await loadUsers();
              const created = result.data?.createdCount || 0;
              const skipped = result.data?.skippedCount || 0;
              setMessageWithAutoClear({
                setMessage,
                type: 'success',
                text: `临时账号处理完成：新增 ${created}，跳过 ${skipped}`,
                clearDelayMs: 5000,
              });
              return { success: true, data: result.data };
            }}
            onUpdateTempUser={async (userId, updates) => {
              const result = await adminUpdateUser(userId, updates);
              if (!result.success) return { error: result.error || '保存失败' };
              await loadUsers();
              return { success: true };
            }}
            onFetchTemplates={getTempScopeTemplates}
            onApplyTemplate={async (userIds, templateId) => {
              const result = await adminApplyTempScopeTemplate(userIds, templateId);
              if (!result.success) return { error: result.error || '应用模板失败' };
              await loadUsers();
              return { success: true, data: result.data };
            }}
            onPreviewTemplate={previewTempScopeTemplate}
            onFetchChangeLogs={getTempScopeChangeLogs}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? 'h-full flex flex-col' : 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'}>
      <div className={contentBoxClass}>
        {!hideHeader && header}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="搜索用户..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all" />
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showOnlyAnomalyUsers}
                onChange={(e) => setShowOnlyAnomalyUsers(e.target.checked)}
                className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500"
              />
              只看异常账号
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={loadUsers} disabled={usersLoading} className="flex items-center gap-2 px-3 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium disabled:opacity-60" title="从服务器重新载入用户列表">
              <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
              {usersLoading ? '载入中...' : '刷新'}
            </button>
            <button onClick={handleSeedPresetUsers} disabled={seeding} className="flex items-center gap-2 px-3 py-2 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors font-medium disabled:opacity-60" title="在服务器数据库中创建预设用户并生成临时强密码（首次登录需改密）">
              {seeding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
              {seeding ? '初始化中...' : '初始化预设用户'}
            </button>
            <button
              onClick={handleBatchResetPasswordsAndExport}
              disabled={user?.role !== 'admin' || batchResetting}
              className="flex items-center gap-2 px-3 py-2 text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors font-medium disabled:opacity-60"
              title="仅管理员：批量重置用户密码并导出一次性初始密码，目标用户首次登录将强制改密"
            >
              {batchResetting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              {batchResetting ? '重置中...' : '批量重置并导出密码'}
            </button>
            <button onClick={() => setShowFinanceTempZone(true)} className="flex items-center gap-2 px-3 py-2 text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors font-medium" title="为财务部三级部门开通临时账号并配置风险/机会操作权限">
              <Shield className="w-4 h-4" />
              临时账号专区
            </button>
            <button
              onClick={() => setShowGroupRuleConfig(true)}
              disabled={user?.role !== 'admin'}
              className="flex items-center gap-2 px-3 py-2 text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors font-medium disabled:opacity-60"
              title="配置账号分组命中规则与显示顺序"
            >
              <Settings className="w-4 h-4" />
              分组规则
            </button>
            <button onClick={() => setShowBatchCreateModal(true)} className="flex items-center gap-2 px-3 py-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors font-medium"><FilePlus className="w-4 h-4" />批量导入</button>
            <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors font-medium shadow-sm"><UserPlus className="w-4 h-4" />新建用户</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6 bg-gray-50/50">
          {message.text && (
            <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
              {message.type === 'success' ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
              {message.text}
            </div>
          )}
          {loadError && (
            <div className="mb-4 p-3 rounded-lg text-sm flex items-center justify-between gap-2 bg-amber-50 text-amber-800 border border-amber-200">
              <span>{loadError}</span>
              <button type="button" onClick={loadUsers} disabled={usersLoading} className="flex items-center gap-1 px-2 py-1 bg-amber-100 hover:bg-amber-200 rounded text-xs font-medium">
                <RefreshCw className={`w-3.5 h-3.5 ${usersLoading ? 'animate-spin' : ''}`} />
                刷新
              </button>
            </div>
          )}
          <div className="space-y-3">
            {groupedUsersForDisplay.map((groupItem) => {
              const collapsed = !!collapsedUserGroupMap[groupItem.id];
              return (
                <div key={groupItem.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleUserGroupCollapse(groupItem.id)}
                    className="w-full px-4 py-3 bg-gray-50/80 border-b border-gray-200 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`px-2 py-0.5 rounded-md border text-xs font-medium ${groupItem.className}`}>
                        {groupItem.label}
                      </span>
                      <span className="text-xs text-gray-500 truncate">{groupItem.hint}</span>
                      <span className="text-xs text-gray-400">({groupItem.users.length})</span>
                    </div>
                    {collapsed ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronUp className="w-4 h-4 text-gray-500" />}
                  </button>
                  {!collapsed && (
                    <div className="p-4 space-y-3 bg-white">
                      {groupItem.users.map(renderUserCard)}
                    </div>
                  )}
                </div>
              );
            })}
            {usersLoading && users.length === 0 && (
              <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
                <RefreshCw className="w-12 h-12 mx-auto mb-3 text-indigo-300 animate-spin" />
                <p>正在载入用户...</p>
              </div>
            )}
            {!usersLoading && groupedUsersForDisplay.length === 0 && (
              <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{users.length === 0 ? '暂无用户数据，请点击上方「刷新」从服务器载入' : (showOnlyAnomalyUsers ? '没有命中异常账号（超配/缺配）' : '没有找到匹配的用户')}</p>
                {users.length === 0 && (
                  <button type="button" onClick={loadUsers} className="mt-3 px-4 py-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg text-sm font-medium">
                    重新载入
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {activePermissionDiag && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setPermissionDiagUserId('')}>
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(event) => event.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-800">权限一致性诊断</h4>
                <p className="text-xs text-gray-500 mt-1">按用户配置：已勾选模块 vs 门户最终可见模块</p>
              </div>
              <button type="button" onClick={() => setPermissionDiagUserId('')} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-auto space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-1">用户</div>
                  <div className="text-sm font-medium text-gray-900">{activePermissionDiag.user.name || activePermissionDiag.user.username || '-'}</div>
                  <div className="text-xs text-gray-500 mt-1">ID：{activePermissionDiag.user.id || '-'}</div>
                </div>
                <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50">
                  <div className="text-xs text-indigo-600 mb-1">权限策略</div>
                  <div className="text-sm font-medium text-indigo-800">{activePermissionDiag.overview.permissionBasisLabel}</div>
                  <div className="text-xs text-indigo-600 mt-1">不与审计角色/审计组挂钩</div>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 text-sm font-medium text-gray-700">一键输出结果</div>
                <pre className="p-3 text-xs text-gray-700 bg-white whitespace-pre-wrap break-words">{activePermissionDiag.overview.diagnosisText}</pre>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 bg-white flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCopyPermissionDiag}
                className="px-4 py-2 text-sm rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 inline-flex items-center gap-1.5"
              >
                <Copy className="w-4 h-4" />
                复制诊断结果
              </button>
              <button type="button" onClick={() => setPermissionDiagUserId('')} className="px-4 py-2 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50">
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
      <UserGroupRuleConfigModal
        isOpen={showGroupRuleConfig}
        onClose={() => setShowGroupRuleConfig(false)}
        rules={userGroupRules}
        setRules={setUserGroupRules}
        onSave={handleSaveUserGroupRules}
        onReset={handleResetUserGroupRules}
        isAdmin={user?.role === 'admin'}
      />
      {showBatchCreateModal && (
        <BatchCreateUserModal
          onClose={() => setShowBatchCreateModal(false)}
          onCreate={async (data) => {
            const flow = await runUserModalMutationFlow({
              execute: () => adminBatchCreateUsers(data),
              reloadUsers: loadUsers,
              onSuccess: (result) => {
                setShowBatchCreateModal(false);
                setMessageWithAutoClear({
                  setMessage,
                  type: 'success',
                  text: buildBatchCreateUsersSuccessMessage(result.count),
                  clearDelayMs: 3000,
                });
              },
            });
            if (!flow.ok) {
              return flow.result;
            }
          }}
        />
      )}
      {showCreateModal && (
        <CreateUserModal 
          onClose={() => setShowCreateModal(false)} 
          onCreate={async (data) => {
            const flow = await runUserModalMutationFlow({
              execute: () => adminCreateUser(data),
              reloadUsers: loadUsers,
              onSuccess: () => {
                setShowCreateModal(false);
                setMessageWithAutoClear({
                  setMessage,
                  type: 'success',
                  text: buildCreateUserSuccessMessage(),
                  clearDelayMs: 3000,
                });
              },
            });
            if (!flow.ok) {
              return flow.result?.error;
            }
          }}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={async (updates) => {
            const flow = await runUserModalMutationFlow({
              execute: () => adminUpdateUser(editingUser.id, updates),
              reloadUsers: loadUsers,
              onSuccess: () => {
                setEditingUser(null);
                setMessageWithAutoClear({
                  setMessage,
                  type: 'success',
                  text: buildUpdateUserSuccessMessage(),
                  clearDelayMs: 3000,
                });
              },
            });
            if (!flow.ok) {
              return flow.result?.error;
            }
          }}
        />
      )}
      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onReset={async (newPassword) => {
            const flow = await runUserModalMutationFlow({
              execute: () => resetUserPassword(resetPasswordUser.id, newPassword),
              shouldReloadUsers: false,
              onSuccess: () => {
                setResetPasswordUser(null);
                setMessageWithAutoClear({
                  setMessage,
                  type: 'success',
                  text: buildResetUserPasswordSuccessMessage(),
                  clearDelayMs: 3000,
                });
              },
            });
            if (!flow.ok) {
              return flow.result?.error;
            }
          }}
        />
      )}
      {showAccountPasswordUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowAccountPasswordUser(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-100" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-800">账号与密码</h4>
              <button type="button" onClick={() => setShowAccountPasswordUser(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-1">用户：{showAccountPasswordUser.name || showAccountPasswordUser.username}</p>
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-500">登录账号</span>
                <span className="font-mono text-sm text-gray-900">{showAccountPasswordUser.username}</span>
              </div>
              <div className="flex items-center justify-between gap-2 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                <span className="text-sm text-amber-800">密码说明</span>
                <span className="text-sm text-amber-900">已改为临时强密码</span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-4">密码已加密存储，无法查看明文。新建预设用户为临时强密码，需首次登录后改密。</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleCopyAccountCredential('account')}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
              >
                <Copy className="w-4 h-4" />复制账号
              </button>
              <button
                type="button"
                onClick={() => handleCopyAccountCredential('accountInfo')}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-cyan-100 hover:bg-cyan-200 rounded-lg text-sm font-medium text-cyan-800"
              >
                <Copy className="w-4 h-4" />复制账号信息
              </button>
              <button type="button" onClick={() => setShowAccountPasswordUser(null)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium text-gray-700">关闭</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FinanceTempAccountZoneModal({
  onClose,
  onSubmit,
  onUpdateTempUser,
  onFetchTemplates,
  onApplyTemplate,
  onPreviewTemplate,
  onFetchChangeLogs,
  users = [],
  embedded = false,
  canEdit = false
}) {
  const [rows, setRows] = useState([
    createEmptyFinanceTempRow()
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resultRows, setResultRows] = useState([]);
  const [orgTree, setOrgTree] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [tempConfigMap, setTempConfigMap] = useState({});
  const [savingTempConfigId, setSavingTempConfigId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [selectedTemplateMap, setSelectedTemplateMap] = useState({});
  const [tempAuditGroupMap, setTempAuditGroupMap] = useState({});
  const [tempAuditDeptPathMap, setTempAuditDeptPathMap] = useState({});
  const [selectedBatchUserIds, setSelectedBatchUserIds] = useState(new Set());
  const [batchTemplateId, setBatchTemplateId] = useState('');
  const [batchApplying, setBatchApplying] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [changeLogs, setChangeLogs] = useState([]);
  const [changeLogLoading, setChangeLogLoading] = useState(false);
  const [logFilterTargetUsername, setLogFilterTargetUsername] = useState('');
  const [logFilterTemplateId, setLogFilterTemplateId] = useState('');
  const [logFilterStartDate, setLogFilterStartDate] = useState('');
  const [logFilterEndDate, setLogFilterEndDate] = useState('');
  const [deptSearchMap, setDeptSearchMap] = useState({});
  const [tempDetailOpenMap, setTempDetailOpenMap] = useState({});
  const tempUsers = useMemo(() => users.filter(isFinanceTempAccountUser), [users]);
  const templateOptions = useMemo(() => buildTemplateSelectOptions(templates), [templates]);

  useEffect(() => {
    let cancelled = false;
    const loadOrgTree = async () => {
      setOrgLoading(true);
      try {
        const token = localStorage.getItem('token');
        const normalized = await loadUserManagementOrgTree(fetch, token);
        if (!cancelled && normalized) setOrgTree(normalized);
      } catch (_) {
        if (!cancelled) setOrgTree(null);
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    };
    loadOrgTree();
    return () => { cancelled = true; };
  }, []);

  const orgDeptOptionState = useMemo(
    () => buildOrgDeptOptionState(orgTree),
    [orgTree]
  );
  const firstLevelOptions = orgDeptOptionState.firstLevelOptions;
  const allOrgDeptOptions = orgDeptOptionState.allOrgDeptOptions;

  useEffect(() => {
    const stateMaps = buildTempScopeStateMaps(tempUsers, orgTree);
    setTempConfigMap(stateMaps.tempConfigMap);
    setSelectedTemplateMap(stateMaps.selectedTemplateMap);
    setTempAuditGroupMap(stateMaps.tempAuditGroupMap);
    setTempAuditDeptPathMap(stateMaps.tempAuditDeptPathMap);
  }, [users, orgTree]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!onFetchTemplates) return;
      setTemplateLoading(true);
      const res = await onFetchTemplates();
      if (!cancelled) {
        if (res?.success) setTemplates(Array.isArray(res.templates) ? res.templates : []);
        else if (res?.error) setError(res.error);
      }
      if (!cancelled) setTemplateLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [onFetchTemplates]);

  const loadChangeLogs = useCallback(async (override = {}) => {
    if (!onFetchChangeLogs) return;
    setChangeLogLoading(true);
    const query = buildTempScopeChangeLogQuery({
      targetUsername: logFilterTargetUsername,
      templateId: logFilterTemplateId,
      startDate: logFilterStartDate,
      endDate: logFilterEndDate,
    }, override);
    const res = await onFetchChangeLogs(query);
    if (res?.success) setChangeLogs(normalizeTempScopeChangeLogItems(res.items));
    else if (res?.error) setError(res.error);
    setChangeLogLoading(false);
  }, [onFetchChangeLogs, logFilterTargetUsername, logFilterTemplateId, logFilterStartDate, logFilterEndDate]);

  useEffect(() => {
    if (!onFetchChangeLogs) return;
    let cancelled = false;
    const run = async () => {
      setChangeLogLoading(true);
      const res = await onFetchChangeLogs(buildTempScopeChangeLogQuery());
      if (!cancelled) {
        if (res?.success) setChangeLogs(normalizeTempScopeChangeLogItems(res.items));
        else if (res?.error) setError(res.error);
        setChangeLogLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [onFetchChangeLogs]);

  const updateRow = (index, key, value) => {
    setRows((prev) => buildUpdatedFinanceTempRows(prev, index, key, value));
  };

  const updateTempAuditDeptPath = (userId, key, value) => {
    setTempAuditDeptPathMap((prev) => {
      const next = buildNextTempAuditDeptPath(prev[userId], key, value);
      const finalDept = resolveFinalDeptFromPath(next);
      setTempAuditGroupMap((groupPrev) => ({ ...groupPrev, [userId]: finalDept }));
      return { ...prev, [userId]: next };
    });
  };

  const addRow = () => setRows((prev) => [...prev, createEmptyFinanceTempRow()]);
  const removeRow = (index) => setRows((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== index));
  const inferFinanceTempTemplateId = useCallback((department) => {
    const normalized = String(department || '').replace(/\s+/g, '').trim();
    if (!normalized) return 'finance-standard';
    if (normalized.includes('资金')) return 'finance-funds';
    if (normalized.includes('市场营销') || normalized.includes('销售')) return 'finance-sales';
    return 'finance-standard';
  }, []);
  const handleAutoMatchCreateTemplates = useCallback(() => {
    setRows((prev) => prev.map((row) => {
      const department = String(row?.deptL3 || row?.deptL2 || row?.deptL1 || row?.department || '').trim();
      return {
        ...row,
        templateId: inferFinanceTempTemplateId(department),
      };
    }));
  }, [inferFinanceTempTemplateId]);
  const handleExportCredentials = () => {
    if (!resultRows.length) return;
    const rowsForExcel = buildFinanceTempCredentialExportRows(resultRows);
    const ws = XLSX.utils.json_to_sheet(rowsForExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '临时账号');
    XLSX.writeFile(wb, buildTimestampedExportFilename('财务临时账号'));
  };

  const handleSubmit = async () => {
    const payload = buildFinanceTempSeedPayload(rows);
    if (!payload.length) {
      setError('请至少填写 1 条账号信息');
      return;
    }
    setSubmitting(true);
    setError('');
    const res = await onSubmit(payload);
    if (res?.error) {
      setError(res.error);
      setSubmitting(false);
      return;
    }
    const creds = Array.isArray(res?.data?.temporaryCredentials) ? res.data.temporaryCredentials : [];
    setResultRows(creds);
    setSubmitting(false);
  };

  const updateTempConfigField = (userId, field, value) => {
    setTempConfigMap((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value,
      }
    }));
  };

  const setDeptKeyword = (userId, value) => {
    setDeptSearchMap((prev) => ({ ...prev, [userId]: String(value || '') }));
  };

  const toggleDeptForUser = (userId, deptName) => {
    updateTempConfigField(
      userId,
      'riskEditableDepartments',
      toggleDeptSelection(tempConfigMap[userId]?.riskEditableDepartments, deptName),
    );
  };

  const addFilteredDeptForUser = (userId, filteredDeptOptions) => {
    updateTempConfigField(
      userId,
      'riskEditableDepartments',
      addDeptSelections(tempConfigMap[userId]?.riskEditableDepartments, filteredDeptOptions),
    );
  };

  const clearDeptForUser = (userId) => {
    updateTempConfigField(userId, 'riskEditableDepartments', []);
  };

  const handleSaveTempConfig = async (u) => {
    if (!onUpdateTempUser || !u?.id) return;
    const draft = tempConfigMap[u.id] || {};
    // 优先使用当前级联选择路径，避免“刚选择部门立即保存”时异步状态尚未写入导致丢失
    const selectedPath = tempAuditDeptPathMap[u.id] || {};
    const auditGroup = String(
      resolveFinalDeptFromPath(selectedPath) || tempAuditGroupMap[u.id] || u.auditGroup || ''
    ).trim();
    if (!auditGroup) {
      setSuccessMessage('');
      setError(buildTempConfigAuditGroupRequiredMessage(u.username));
      return;
    }
    const { nextEditableDepts, requestBody } = buildTempConfigSavePayload({
      draft,
      auditGroup,
      auditDeptPath: selectedPath,
      selectedTemplateId: selectedTemplateMap[u.id],
      templates,
    });
    setSavingTempConfigId(u.id);
    setSuccessMessage('');
    setError('');
    const res = await onUpdateTempUser(u.id, requestBody);
    if (res?.error) {
      setSuccessMessage('');
      setError(buildTempConfigSaveFailedMessage(u.username, res.error));
    } else {
      setTempAuditGroupMap((prev) => ({ ...prev, [u.id]: auditGroup }));
      setTempAuditDeptPathMap((prev) => ({ ...prev, [u.id]: { ...selectedPath } }));
      updateTempConfigField(u.id, 'riskEditableDepartments', nextEditableDepts);
      setSuccessMessage(buildTempConfigSaveSuccessMessage(u.username));
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(buildTempConfigSaveSuccessAlertMessage());
      }
      setTimeout(() => setSuccessMessage(''), 2200);
    }
    if (!res?.error) await loadChangeLogs();
    setSavingTempConfigId('');
  };

  const toggleBatchUser = (userId) => {
    setSelectedBatchUserIds((prev) => buildNextSelectedBatchUserIds(prev, userId));
  };

  const handleTemplateSelectChange = useCallback((userId, templateId) => {
    setSelectedTemplateMap((prev) => ({ ...prev, [userId]: templateId }));
  }, []);

  const handleToggleRiskVisibleTab = useCallback((userId, currentTabs, tabId) => {
    updateTempConfigField(userId, 'riskVisibleTabs', toggleStringSelection(currentTabs, tabId));
  }, []);

  const handleToggleInternalVisibleDraftGroup = useCallback((userId, currentGroups, groupName) => {
    updateTempConfigField(userId, 'internalVisibleDraftGroups', toggleStringSelection(currentGroups, groupName));
  }, []);
  const handleToggleInternalVisibleMode = useCallback((userId, currentModes, modeId) => {
    updateTempConfigField(userId, 'internalVisibleModes', toggleStringSelection(currentModes, modeId));
  }, []);
  const handleSetInternalDraftEvalPermission = useCallback((userId, permissionId) => {
    updateTempConfigField(userId, 'internalDraftEvalPermission', permissionId);
  }, []);

  const handleApplyTemplateForUser = async (u) => {
    if (!onApplyTemplate) return;
    const templateId = resolveTemplateIdForUser(selectedTemplateMap, u.id, batchTemplateId);
    const validationError = validateSingleTemplateAction(templateId, buildApplyTemplateSelectFirstMessage());
    if (validationError) {
      setError(validationError);
      return;
    }
    await runTemplateActionWithLoading({
      setLoading: setBatchApplying,
      setError,
      execute: () => onApplyTemplate([u.id], templateId),
      onSuccess: async () => {
        await loadChangeLogs();
      },
    });
  };

  const handlePreviewTemplateForUser = async (u) => {
    if (!onPreviewTemplate) return;
    const templateId = resolveTemplateIdForUser(selectedTemplateMap, u.id, batchTemplateId);
    const validationError = validateSingleTemplateAction(templateId, buildPreviewTemplateSelectFirstMessage());
    if (validationError) {
      setError(validationError);
      return;
    }
    await runTemplateActionWithLoading({
      setLoading: setPreviewLoading,
      setError,
      execute: () => onPreviewTemplate([u.id], templateId),
      onSuccess: (res) => {
        setPreviewRows(normalizeTemplatePreviewRows(res.items));
      },
    });
  };

  const handlePreviewTemplateBatch = async () => {
    if (!onPreviewTemplate) return;
    const userIds = getSelectedBatchUserIds(selectedBatchUserIds);
    const validationError = validateBatchTemplateAction({
      userIds,
      batchTemplateId,
      missingUsersMessage: buildPreviewTemplateSelectUsersFirstMessage(),
      missingTemplateMessage: buildBatchTemplateSelectFirstMessage(),
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    await runTemplateActionWithLoading({
      setLoading: setPreviewLoading,
      setError,
      execute: () => onPreviewTemplate(userIds, batchTemplateId),
      onSuccess: (res) => {
        setPreviewRows(normalizeTemplatePreviewRows(res.items));
      },
    });
  };

  const handleApplyTemplateBatch = async () => {
    if (!onApplyTemplate) return;
    const userIds = getSelectedBatchUserIds(selectedBatchUserIds);
    const validationError = validateBatchTemplateAction({
      userIds,
      batchTemplateId,
      missingUsersMessage: buildApplyTemplateSelectUsersFirstMessage(),
      missingTemplateMessage: buildBatchTemplateSelectFirstMessage(),
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    await runTemplateActionWithLoading({
      setLoading: setBatchApplying,
      setError,
      execute: () => onApplyTemplate(userIds, batchTemplateId),
      onSuccess: async () => {
        setSelectedBatchUserIds(new Set());
        await loadChangeLogs();
      },
    });
  };
  const handleAutoMatchAndApplyTempUsers = useCallback(async () => {
    if (!onApplyTemplate) return;
    if (!tempUsers.length) {
      setError('暂无可匹配的临时账号');
      return;
    }
    const groupedUserIds = {};
    const nextTemplateSelections = {};
    tempUsers.forEach((u) => {
      const auditPath = tempAuditDeptPathMap[u.id] || {};
      const department = String(
        resolveFinalDeptFromPath(auditPath) || tempAuditGroupMap[u.id] || u.auditGroup || ''
      ).trim();
      const templateId = inferFinanceTempTemplateId(department);
      if (!groupedUserIds[templateId]) groupedUserIds[templateId] = [];
      groupedUserIds[templateId].push(u.id);
      nextTemplateSelections[u.id] = templateId;
    });
    const planEntries = Object.entries(groupedUserIds).filter(([, ids]) => ids.length > 0);
    if (!planEntries.length) {
      setError('未识别到可自动匹配的临时账号');
      return;
    }
    setBatchApplying(true);
    setError('');
    setSuccessMessage('');
    const failedTemplates = [];
    for (const [templateId, userIds] of planEntries) {
      const res = await onApplyTemplate(userIds, templateId);
      if (res?.error) failedTemplates.push(`${templateId}：${res.error}`);
    }
    setSelectedTemplateMap((prev) => ({ ...prev, ...nextTemplateSelections }));
    if (failedTemplates.length) {
      setError(`自动匹配完成，但部分模板应用失败：${failedTemplates.join('；')}`);
    } else {
      setSuccessMessage(`已按部门自动匹配并应用 ${tempUsers.length} 个临时账号的权限模板`);
      setTimeout(() => setSuccessMessage(''), 2200);
    }
    await loadChangeLogs();
    setBatchApplying(false);
  }, [
    onApplyTemplate,
    tempUsers,
    tempAuditDeptPathMap,
    tempAuditGroupMap,
    inferFinanceTempTemplateId,
    loadChangeLogs,
  ]);

  const handleExportChangeLogs = () => {
    if (!changeLogs.length) return;
    const rowsForExcel = buildTempScopeChangeLogExportRows(changeLogs);
    const ws = XLSX.utils.json_to_sheet(rowsForExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '模板变更日志');
    XLSX.writeFile(wb, buildTimestampedExportFilename('临时账号模板变更日志'));
  };

  const toggleTempDetail = (userId) => {
    setTempDetailOpenMap((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  return (
    <div className={embedded ? 'h-full flex flex-col min-h-0' : 'fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4'}>
      <div className={embedded ? 'bg-white border border-gray-100 rounded-xl shadow-sm flex-1 min-h-0 overflow-hidden flex flex-col' : 'bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col'}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">临时账号管理专区</h4>
            <p className="text-xs text-gray-500 mt-1">用于财务三级部门临时账号开通与风险/机会权限分配</p>
          </div>
          <button onClick={onClose} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-gray-100 text-sm text-gray-700">
            {embedded ? <ChevronLeft className="w-4 h-4" /> : <X className="w-5 h-5" />}
            {embedded ? '返回' : '关闭'}
          </button>
        </div>
        <div className="p-6 overflow-auto space-y-4">
          {error && <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">{error}</div>}
          {successMessage && <div className="p-3 rounded-lg border border-green-200 bg-green-50 text-green-700 text-sm">{successMessage}</div>}
          <div className="p-3 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-800 text-sm">
            权限说明：`浏览`=仅查看；`编辑`=可新增/编辑财务范围风险与机会；`管理`=可新增/编辑/删除财务范围风险与机会。
          </div>
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">用户名</th>
                  <th className="px-3 py-2 text-left">姓名</th>
                  <th className="px-3 py-2 text-left">所属部门（可选到三级）</th>
                  <th className="px-3 py-2 text-left">权限等级</th>
                  <th className="px-3 py-2 text-left">策略模板</th>
                  <th className="px-3 py-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, idx) => {
                  const cascadeOptions = buildDeptCascadeOptions(orgTree, row);
                  return (
                  <tr key={idx}>
                    <td className="px-3 py-2"><input value={row.username} onChange={(e) => updateRow(idx, 'username', e.target.value)} placeholder="finance_xxx_tmp" className="w-full px-3 py-2 border rounded-lg" /></td>
                    <td className="px-3 py-2"><input value={row.name} onChange={(e) => updateRow(idx, 'name', e.target.value)} placeholder="人员姓名或岗位名" className="w-full px-3 py-2 border rounded-lg" /></td>
                    <td className="px-3 py-2">
                      <div className="grid grid-cols-1 gap-2">
                        <select value={row.deptL1 || ''} onChange={(e) => updateRow(idx, 'deptL1', e.target.value)} className="w-full px-3 py-2 border rounded-lg bg-white text-xs">
                          <option value="">请选择一级部门</option>
                          {firstLevelOptions.map((name) => <option key={`l1-${name}`} value={name}>{name}</option>)}
                        </select>
                        <select value={row.deptL2 || ''} onChange={(e) => updateRow(idx, 'deptL2', e.target.value)} disabled={cascadeOptions.isSecondLevelDisabled} className="w-full px-3 py-2 border rounded-lg bg-white text-xs disabled:bg-gray-100">
                          <option value="">请选择二级部门</option>
                          {cascadeOptions.secondLevelOptions.map((name) => <option key={`l2-${row.deptL1}-${name}`} value={name}>{name}</option>)}
                        </select>
                        <select value={row.deptL3 || ''} onChange={(e) => updateRow(idx, 'deptL3', e.target.value)} disabled={cascadeOptions.isThirdLevelDisabled} className="w-full px-3 py-2 border rounded-lg bg-white text-xs disabled:bg-gray-100">
                          <option value="">请选择三级部门（可选）</option>
                          {cascadeOptions.thirdLevelOptions.map((name) => <option key={`l3-${row.deptL2}-${name}`} value={name}>{name}</option>)}
                        </select>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <select value={row.auditRole} onChange={(e) => updateRow(idx, 'auditRole', e.target.value)} className="w-full px-3 py-2 border rounded-lg bg-white">
                        {FINANCE_TEMP_AUDIT_ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select value={row.templateId || ''} onChange={(e) => updateRow(idx, 'templateId', e.target.value)} className="w-full px-3 py-2 border rounded-lg bg-white">
                        <option value="">{buildTemplateSelectPlaceholder('autoMatch', templateLoading)}</option>
                        {templateOptions.map((tpl) => <option key={`create-${tpl.id}`} value={tpl.id}>{tpl.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => removeRow(idx)} className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200">删除行</button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-gray-500">
            {buildFinanceOrgTreeHintMessage(orgLoading, firstLevelOptions)}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={addRow} className="px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm">+ 新增一行</button>
            <button onClick={handleAutoMatchCreateTemplates} className="px-3 py-2 rounded-lg bg-cyan-50 text-cyan-700 hover:bg-cyan-100 text-sm">一键匹配权限模板</button>
            <button onClick={handleSubmit} disabled={submitting} className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-60">{submitting ? '提交中...' : '批量开通账号'}</button>
            <button onClick={handleExportCredentials} disabled={!resultRows.length} className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm disabled:opacity-60">导出账号+初始密码（Excel）</button>
          </div>

          {!!resultRows.length && (
            <div className="border rounded-xl overflow-hidden">
              <div className="px-3 py-2 border-b bg-emerald-50 text-emerald-700 text-sm font-medium">本次新建账号凭证（请及时分发并要求首次改密）</div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">账号</th>
                    <th className="px-3 py-2 text-left">临时密码</th>
                    <th className="px-3 py-2 text-left">权限</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {resultRows.map((r, i) => (
                    <tr key={`${r.username}-${i}`}>
                      <td className="px-3 py-2 font-mono">{r.username}</td>
                      <td className="px-3 py-2 font-mono">{r.tempPassword}</td>
                      <td className="px-3 py-2">{r.auditRole}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="border rounded-xl p-3 bg-gray-50 space-y-3">
            <div className="text-sm font-medium text-gray-700">已存在临时账号（{tempUsers.length}）</div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-indigo-800">模板化策略（批量）</span>
              <select
                value={batchTemplateId}
                onChange={(e) => setBatchTemplateId(e.target.value)}
                className="px-2 py-1.5 text-xs border rounded bg-white min-w-[180px]"
              >
                <option value="">{buildTemplateSelectPlaceholder('batch', templateLoading)}</option>
                {templateOptions.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
              </select>
              <button
                type="button"
                onClick={handleAutoMatchAndApplyTempUsers}
                disabled={batchApplying || !tempUsers.length}
                className="px-3 py-1.5 rounded bg-cyan-600 text-white text-xs hover:bg-cyan-700 disabled:opacity-60"
              >
                {batchApplying ? '应用中...' : `按部门一键匹配权限（${tempUsers.length}）`}
              </button>
              <button
                type="button"
                onClick={handlePreviewTemplateBatch}
                disabled={previewLoading || !batchTemplateId}
                className="px-3 py-1.5 rounded bg-white text-indigo-700 border border-indigo-300 text-xs hover:bg-indigo-100 disabled:opacity-60"
              >
                {previewLoading ? '预览中...' : `预览已勾选账号差异（${selectedBatchUserIds.size}）`}
              </button>
              <button
                type="button"
                onClick={handleApplyTemplateBatch}
                disabled={batchApplying || !batchTemplateId}
                className="px-3 py-1.5 rounded bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-60"
              >
                {batchApplying ? '应用中...' : `应用到已勾选账号（${selectedBatchUserIds.size}）`}
              </button>
            </div>
            {!tempUsers.length && (
              <div className="text-xs text-gray-600">暂无</div>
            )}
            {!!tempUsers.length && (
              <div className="space-y-3">
                {tempUsers.map((u) => {
                  const {
                    cfg,
                    auditPath,
                    filteredDeptOptions,
                    selectedDeptList,
                    displayAuditGroup,
                  } = buildTempUserCardViewState({
                    user: u,
                    tempConfigMap,
                    tempAuditDeptPathMap,
                    tempAuditGroupMap,
                    deptSearchMap,
                    allOrgDeptOptions,
                  });
                  const auditCascadeOptions = buildDeptCascadeOptions(orgTree, auditPath);
                  const finalDeptDisplay = resolveFinalDeptFromPath(auditPath) || displayAuditGroup || '未设置';
                  const riskTabLabels = RISK_TAB_OPTIONS
                    .filter((tab) => (cfg.riskVisibleTabs || []).includes(tab.id))
                    .map((tab) => tab.label);
                  const internalModeLabels = INTERNAL_CONTROL_MODE_OPTIONS
                    .filter((mode) => (cfg.internalVisibleModes || []).includes(mode.id))
                    .map((mode) => mode.label);
                  const detailOpen = !!tempDetailOpenMap[u.id];
                  return (
                    <div key={u.id} className="rounded-xl border border-gray-200 bg-white p-4 space-y-3 shadow-sm">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="text-sm text-gray-800 flex flex-wrap items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedBatchUserIds.has(u.id)}
                            onChange={() => toggleBatchUser(u.id)}
                            className="w-4 h-4"
                            title="加入批量模板应用"
                          />
                          <span className="font-mono">{u.username}</span>
                          <span className="mx-1 text-gray-300">|</span>
                          <span>{u.auditRole || 'finance-temp'}</span>
                          <span className="mx-1 text-gray-300">|</span>
                          <span>{displayAuditGroup}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {canEdit ? (
                            <>
                              <button
                                type="button"
                                onClick={() => toggleTempDetail(u.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-indigo-300 bg-white text-indigo-700 text-xs hover:bg-indigo-50"
                              >
                                {detailOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                {detailOpen ? '收起详细编辑' : '编辑详细权限'}
                              </button>
                              <select
                                value={selectedTemplateMap[u.id] || ''}
                                onChange={(e) => handleTemplateSelectChange(u.id, e.target.value)}
                                className="px-2 py-1.5 text-xs border rounded bg-white min-w-[160px]"
                              >
                                <option value="">{buildTemplateSelectPlaceholder('single', templateLoading)}</option>
                                {templateOptions.map((tpl) => <option key={`${u.id}-${tpl.id}`} value={tpl.id}>{tpl.name}</option>)}
                              </select>
                              <button
                                onClick={() => handlePreviewTemplateForUser(u)}
                                disabled={previewLoading || !selectedTemplateMap[u.id]}
                                className="px-3 py-1.5 rounded-lg bg-white text-violet-700 border border-violet-300 text-xs hover:bg-violet-50 disabled:opacity-60"
                              >
                                预览差异
                              </button>
                              <button
                                onClick={() => handleApplyTemplateForUser(u)}
                                disabled={batchApplying || !selectedTemplateMap[u.id]}
                                className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs hover:bg-violet-700 disabled:opacity-60"
                              >
                                应用模板
                              </button>
                              <button
                                onClick={() => handleSaveTempConfig(u)}
                                disabled={savingTempConfigId === u.id}
                                className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-60"
                              >
                                {savingTempConfigId === u.id ? '保存中...' : '保存配置'}
                              </button>
                            </>
                          ) : (
                            <span className="px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs">仅管理员可编辑</span>
                          )}
                        </div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="text-xs text-slate-500 mb-1">已保存配置（展示）</div>
                        <div className="text-xs text-slate-700 flex flex-wrap items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">部门：{finalDeptDisplay}</span>
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">风险页签：{riskTabLabels.join('、') || '未设置'}</span>
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">内控窗口：{internalModeLabels.join('、') || '未设置'}</span>
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">底稿权限：{(INTERNAL_DRAFT_EVAL_PERMISSION_OPTIONS.find((x) => x.id === (cfg.internalDraftEvalPermission || 'edit')) || {}).label || '编辑'}</span>
                          <span className="px-2 py-0.5 rounded bg-white border border-slate-200">可编辑部门数：{(cfg.riskEditableDepartments || []).length}</span>
                        </div>
                      </div>
                      {canEdit && detailOpen && (
                      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
                        <div className="xl:col-span-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs font-medium text-gray-700 mb-1">所属部门（权限范围基准，可逐级）</div>
                          <div className="text-[11px] text-gray-500 mb-2">建议优先选择到三级部门，权限会更精准。</div>
                          <div className="grid grid-cols-1 gap-2">
                            <select
                              value={auditPath.deptL1 || ''}
                              onChange={(e) => updateTempAuditDeptPath(u.id, 'deptL1', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border rounded bg-white"
                            >
                              <option value="">请选择一级部门</option>
                              {firstLevelOptions.map((name) => <option key={`${u.id}-l1-${name}`} value={name}>{name}</option>)}
                            </select>
                            <select
                              value={auditPath.deptL2 || ''}
                              onChange={(e) => updateTempAuditDeptPath(u.id, 'deptL2', e.target.value)}
                              disabled={auditCascadeOptions.isSecondLevelDisabled}
                              className="w-full px-2 py-1.5 text-xs border rounded bg-white disabled:bg-gray-100"
                            >
                              <option value="">请选择二级部门</option>
                              {auditCascadeOptions.secondLevelOptions.map((name) => <option key={`${u.id}-l2-${auditPath.deptL1}-${name}`} value={name}>{name}</option>)}
                            </select>
                            <select
                              value={auditPath.deptL3 || ''}
                              onChange={(e) => updateTempAuditDeptPath(u.id, 'deptL3', e.target.value)}
                              disabled={auditCascadeOptions.isThirdLevelDisabled}
                              className="w-full px-2 py-1.5 text-xs border rounded bg-white disabled:bg-gray-100"
                            >
                              <option value="">请选择三级部门（可选）</option>
                              {auditCascadeOptions.thirdLevelOptions.map((name) => <option key={`${u.id}-l3-${auditPath.deptL2}-${name}`} value={name}>{name}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="xl:col-span-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs font-medium text-gray-700 mb-2">风险矩阵可见页签</div>
                          <div className="flex flex-wrap gap-1.5">
                            {RISK_TAB_OPTIONS.map((tab) => {
                              const active = (cfg.riskVisibleTabs || []).includes(tab.id);
                              return (
                                <button
                                  key={`${u.id}-${tab.id}`}
                                  type="button"
                                  onClick={() => handleToggleRiskVisibleTab(u.id, cfg.riskVisibleTabs, tab.id)}
                                  className={`px-2 py-1 rounded text-xs border ${active ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-white text-gray-600 border-gray-300'}`}
                                >
                                  {tab.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="xl:col-span-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs font-medium text-gray-700 mb-2">内控模块可见范围</div>
                          <div className="flex flex-wrap gap-1.5">
                            {INTERNAL_CONTROL_MODULE_OPTIONS.map((name) => {
                              const active = (cfg.internalVisibleDraftGroups || []).includes(name);
                              return (
                                <button
                                  key={`${u.id}-ic-${name}`}
                                  type="button"
                                  onClick={() => handleToggleInternalVisibleDraftGroup(u.id, cfg.internalVisibleDraftGroups, name)}
                                  className={`px-2 py-1 rounded text-xs border ${active ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-white text-gray-600 border-gray-300'}`}
                                >
                                  {name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="xl:col-span-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs font-medium text-gray-700 mb-2">内控窗口可见权限</div>
                          <div className="flex flex-wrap gap-1.5">
                            {INTERNAL_CONTROL_MODE_OPTIONS.map((mode) => {
                              const active = (cfg.internalVisibleModes || []).includes(mode.id);
                              return (
                                <button
                                  key={`${u.id}-ic-mode-${mode.id}`}
                                  type="button"
                                  onClick={() => handleToggleInternalVisibleMode(u.id, cfg.internalVisibleModes, mode.id)}
                                  className={`px-2 py-1 rounded text-xs border ${active ? 'bg-sky-50 text-sky-700 border-sky-300' : 'bg-white text-gray-600 border-gray-300'}`}
                                >
                                  {mode.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="xl:col-span-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <div className="text-xs font-medium text-gray-700 mb-2">内控底稿评价权限</div>
                          <div className="flex flex-wrap gap-1.5">
                            {INTERNAL_DRAFT_EVAL_PERMISSION_OPTIONS.map((item) => {
                              const active = (cfg.internalDraftEvalPermission || 'edit') === item.id;
                              return (
                                <button
                                  key={`${u.id}-ic-perm-${item.id}`}
                                  type="button"
                                  onClick={() => handleSetInternalDraftEvalPermission(u.id, item.id)}
                                  className={`px-2 py-1 rounded text-xs border ${active ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-white text-gray-600 border-gray-300'}`}
                                >
                                  {item.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="xl:col-span-12 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3">
                          <div className="text-xs font-medium text-gray-700 mb-1">风险可编辑部门（可多选）</div>
                          <div className="text-[11px] text-gray-500 mb-2">先筛选再批量加入，最后可在下方已选标签里快速删除。</div>
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                value={deptSearchMap[u.id] || ''}
                                onChange={(e) => setDeptKeyword(u.id, e.target.value)}
                                placeholder="输入部门关键词快速筛选"
                                className="flex-1 px-2 py-1.5 text-xs border rounded bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => addFilteredDeptForUser(u.id, filteredDeptOptions)}
                                disabled={!filteredDeptOptions.length}
                                className="px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-60"
                                title="将当前筛选结果加入已选"
                              >
                                加入筛选结果
                              </button>
                              <button
                                type="button"
                                onClick={() => clearDeptForUser(u.id)}
                                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                              >
                                清空
                              </button>
                            </div>
                            <div className="rounded border bg-white p-2 max-h-[180px] overflow-auto space-y-1">
                              {!filteredDeptOptions.length && (
                                <div className="text-[11px] text-gray-400 px-1">无匹配部门</div>
                              )}
                              {filteredDeptOptions.map((name) => (
                                <label key={`${u.id}-dept-opt-${name}`} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedDeptList.includes(name)}
                                    onChange={() => toggleDeptForUser(u.id, name)}
                                    className="w-3.5 h-3.5"
                                  />
                                  <span className="text-xs text-gray-700">{name}</span>
                                </label>
                              ))}
                            </div>
                            <div className="flex flex-wrap gap-1.5 min-h-[24px]">
                              {(cfg.riskEditableDepartments || []).map((name) => (
                                <span key={`${u.id}-dept-selected-${name}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-[11px] text-indigo-700">
                                  {name}
                                  <button
                                    type="button"
                                    onClick={() => toggleDeptForUser(u.id, name)}
                                    className="hover:text-red-600"
                                    title="移除"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                              {!(cfg.riskEditableDepartments || []).length && (
                                <span className="text-[11px] text-gray-400">未选择部门</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs font-medium text-amber-800 mb-2">模板预览差异（应用前）</div>
              {previewLoading && <div className="text-xs text-amber-700">正在计算差异...</div>}
              {!previewLoading && !previewRows.length && <div className="text-xs text-amber-700">点击“预览差异”后显示对比结果。</div>}
              {!previewLoading && !!previewRows.length && (
                <div className="max-h-52 overflow-auto space-y-2">
                  {previewRows.map((row, idx) => (
                    <div key={`${row.userId || idx}-${idx}`} className="bg-white border border-amber-200 rounded p-2 text-xs text-gray-700">
                      <div className="font-medium text-gray-800 mb-1">{row.username}（{row.auditGroup || '未分组'}）</div>
                      <div>模板：{row.diff?.template?.before?.name || row.diff?.template?.before?.id || '未设置'} → {row.diff?.template?.after?.name || row.diff?.template?.after?.id || '未设置'}</div>
                      <div>风险可见页签：{(row.diff?.riskVisibleTabs?.before || []).join('、') || '—'} → {(row.diff?.riskVisibleTabs?.after || []).join('、') || '—'}</div>
                      <div>风险可编辑部门：{(row.diff?.riskEditableDepartments?.before || []).join('、') || '—'} → {(row.diff?.riskEditableDepartments?.after || []).join('、') || '—'}</div>
                      <div>内控可见模块：{(row.diff?.internalVisibleDraftGroups?.before || []).join('、') || '—'} → {(row.diff?.internalVisibleDraftGroups?.after || []).join('、') || '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-medium text-slate-700 mb-2">模板策略变更日志（可筛选，默认最近200条）</div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
                <input
                  value={logFilterTargetUsername}
                  onChange={(e) => setLogFilterTargetUsername(e.target.value)}
                  placeholder="按账号筛选"
                  className="px-2 py-1.5 text-xs border rounded bg-white"
                />
                <select
                  value={logFilterTemplateId}
                  onChange={(e) => setLogFilterTemplateId(e.target.value)}
                  className="px-2 py-1.5 text-xs border rounded bg-white"
                >
                  <option value="">全部模板</option>
                  {templateOptions.map((tpl) => <option key={`log-tpl-${tpl.id}`} value={tpl.id}>{tpl.name}</option>)}
                </select>
                <input
                  type="date"
                  value={logFilterStartDate}
                  onChange={(e) => setLogFilterStartDate(e.target.value)}
                  className="px-2 py-1.5 text-xs border rounded bg-white"
                />
                <input
                  type="date"
                  value={logFilterEndDate}
                  onChange={(e) => setLogFilterEndDate(e.target.value)}
                  className="px-2 py-1.5 text-xs border rounded bg-white"
                />
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => loadChangeLogs()}
                    className="px-2 py-1.5 text-xs rounded bg-slate-700 text-white hover:bg-slate-800"
                  >
                    筛选
                  </button>
                  <button
                    type="button"
                    onClick={handleExportChangeLogs}
                    disabled={!changeLogs.length}
                    className="px-2 py-1.5 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    导出Excel
                  </button>
                </div>
              </div>
              {changeLogLoading && <div className="text-xs text-slate-600">加载中...</div>}
              {!changeLogLoading && !changeLogs.length && <div className="text-xs text-slate-600">暂无日志</div>}
              {!changeLogLoading && !!changeLogs.length && (
                <div className="max-h-52 overflow-auto space-y-1.5">
                  {changeLogs.map((log) => (
                    <div key={log.id} className="text-xs text-slate-700 bg-white border border-slate-200 rounded px-2 py-1.5">
                      <span className="font-medium">{log.target?.username || '-'}</span>
                      <span className="mx-1">|</span>
                      <span>{log.template?.templateName || log.template?.templateId || '模板'}</span>
                      <span className="mx-1">|</span>
                      <span>v{log.template?.templateVersion || '-'}</span>
                      <span className="mx-1">|</span>
                      <span>{log.operator?.username || '-'}</span>
                      <span className="mx-1">|</span>
                      <span>{log.timestamp ? new Date(log.timestamp).toLocaleString('zh-CN') : '-'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 新建用户弹窗（专栏权限仅支持按用户：创建后在列表中勾选模块）
function CreateUserModal({ onClose, onCreate }) {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('user');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateCreateUserForm({
      username,
      password,
      validatePasswordPolicyFn: validatePasswordPolicy,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    const err = await onCreate({
      username,
      password,
      ...buildUserUpdatePayload({ name, email, role }),
    });
    if (err) setError(err);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h4 className="font-semibold text-gray-800">新建用户</h4>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">用户名 *</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">姓名</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">密码 *</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 pr-10" required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">平台角色</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">
              {CREATE_USER_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-500">新建账号固定为「普通用户」；系统管理员仅主账号（默认登录名 admin，可用环境变量 PRIMARY_ADMIN_USERNAME / 构建时 VITE_PRIMARY_ADMIN_USERNAME 对齐）。专栏访问在用户卡片中按模块勾选。</p>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">取消</button>
            <button type="submit" className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">创建</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// 编辑用户弹窗
function EditUserModal({ user, onClose, onSave }) {
  const roleOptions = useMemo(() => getEditUserRoleOptions(user?.username), [user?.username]);
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [role, setRole] = useState(() => {
    const r = normalizePlatformRoleForEdit(user.role);
    const opts = getEditUserRoleOptions(user?.username);
    return opts.some((o) => o.value === r) ? r : 'user';
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateEditUserForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    const updates = buildUserUpdatePayload({ name, email, role });
    const err = await onSave(updates);
    if (err) setError(err);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h4 className="font-semibold text-gray-800">编辑用户</h4>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
          <div><label className="block text-sm font-medium text-gray-700 mb-1">用户名</label><input type="text" value={user.username} className="w-full px-4 py-2 border rounded-lg bg-gray-50" disabled /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">姓名</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">邮箱</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">平台角色</label><select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500">{roleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
          <p className="text-xs text-gray-500">专栏权限在用户卡片中勾选模块；此处不配置审计组/审计角色。</p>
          <div className="flex gap-3 pt-2"><button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">取消</button><button type="submit" className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">保存</button></div>
        </form>
      </div>
    </div>
  );
}

// 重置密码弹窗
function ResetPasswordModal({ user, onClose, onReset }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateResetPasswordForm({
      newPassword,
      confirmPassword,
      username: user?.username,
      validatePasswordPolicyFn: validatePasswordPolicy,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    const err = await onReset(newPassword);
    if (err) setError(err);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-6 py-4 border-b flex items-center justify-between"><h4 className="font-semibold text-gray-800">重置密码</h4><button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button></div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-600">为用户 <span className="font-medium">{user.username}</span> 设置新密码</p>
          {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
            <div className="relative"><input type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 pr-10" required /><button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">确认密码</label>
            <div className="relative"><input type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 pr-10" required /><button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div>
          </div>
          <div className="flex gap-3 pt-2"><button type="button" onClick={onClose} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">取消</button><button type="submit" className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">确认</button></div>
        </form>
      </div>
    </div>
  );
}

// UserManagementPage：作为主内容区普通页面使用（非弹窗），需传入 onClose 用于返回；hideHeader 用于嵌入统一用户管理系统时隐藏本页标题
export function UserManagementPage({ onClose, hideHeader }) {
    return <AdminUserManagement onClose={onClose ?? (() => {})} embedded={true} hideHeader={hideHeader} />;
}

export default { UserProvider, useUser, LoginPage, UserMenu, AdminUserManagement };
